import { forwardRef, memo, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type DragEvent, type KeyboardEvent, type MouseEvent, type PointerEvent, type WheelEvent } from "react";
import { createPortal } from "react-dom";
import {
  ANIMATION_EASING_NAMES,
  ANIMATION_TRANSITION_EFFECTS,
  DEFAULT_TRANSITION_COLOR,
  DEFAULT_WIGGLE_EASING,
  createAnimationGraphNode,
  isAnimationNodeCollapsed,
  isClampedDistancePinTarget,
  isDistancePinTarget,
  distancePinSnapBack,
  type AnimationEasingName,
  type AnimationGroupRegroupMode,
  type AnimationTransitionEffectName,
  type AnimationConnection,
  type AnimationEndpoint,
  type AnimationGraph,
  type AnimationGraphNode,
  type AnimationGraphVisualGroup,
  type AnimationGroupNumericOverride,
  type AnimationAnchor,
  type AnimationNodeKind,
  type AnimationPinKind,
  type AnimationProperty,
  type ColorAnimationSpace,
  type EffectAnimationField,
  type GroupAnimationNode,
  type MotionAnimationNode,
} from "@/document/model/animation";
import { animationEasingFunction } from "@/document/animation/easing";
import {
  allColorAnimationChannels,
  animationPropertyWithColorChannelValue,
  animationPropertyWithColorSpace,
  colorAnimationChannelFields,
} from "@/document/animation/colorAnimationFields";
import { splitTextNodeForRegroup } from "@/document/animation/applyAnimation";
import { findSceneNode, getAllSceneNodes, type CurvaDocument } from "@/document/model/document";
import type { FrameNode, SceneNode } from "@/document/model/nodes";
import type { NodeStyle, Paint } from "@/document/model/styles";
import { createSolidPaint, normalizeStrokeStack } from "@/document/model/styles";
import { oklchToHex, type OklchColor } from "@/document/model/color";
import type { Camera } from "@/renderer/camera";
import type { ViewportScreenInsets } from "@/renderer/renderHelpers";
import { createId } from "@/lib/ids";
import { nodeSelectionPointToWorld } from "@/document/operations/nodeWorldTransform";
import { animationTargetKey } from "@/editor/shell/animationTargetFromSelection";
import { ArrowUpDown, Check, ChevronDown, ChevronRight, GripVertical, Pause, Pencil, Plus, Play, RotateCcw, Search, Shuffle, Trash2, Ungroup, Workflow, X } from "lucide-react";
import { hitTestDocument } from "@/editor/tools/hitTest";
import { Metric } from "./right-panel/Metric";
import { PanelSelect, PanelSelectItem, panelFloatingUiAttribute, panelSelectTriggerClass, panelSelectContentClass } from "./right-panel/PanelSelect";
import { SearchableFloatingSelect } from "./right-panel/SearchableFloatingSelect";
import { ColorPickerPopover } from "./right-panel/ColorPickerPopover";
import { paintPreviewBackground } from "./right-panel/paintStyles";
import { deepSelectModifier } from "./canvasGeometry";

interface AnimationGraphOverlayProps {
  graph: AnimationGraph;
  document: CurvaDocument;
  camera: Camera;
  active: boolean;
  selectedNodeIds: string[];
  selectedConnectionIds: string[];
  selectedObjectConnectionKeys: string[];
  frames: FrameNode[];
  playing: boolean;
  objectDragGhost?: { point: { x: number; y: number }; kind: AnimationNodeKind };
  onInsertNode: (node: AnimationGraphNode) => void;
  onSelectNodes: (nodeIds: string[], primaryNodeId?: string) => void;
  onSelectConnections: (connectionIds: string[]) => void;
  onSelectObjectConnections: (keys: string[]) => void;
  onMoveNodes: (before: Array<{ nodeId: string; position: { x: number; y: number } }>, after: Array<{ nodeId: string; position: { x: number; y: number } }>) => void;
  onDuplicateNodes: (after: Array<{ nodeId: string; position: { x: number; y: number } }>) => void;
  onConnect: (connection: AnimationConnection) => void;
  onObjectConnect?: (sourceNodeId: string, endpoint: AnimationEndpoint) => void;
  onInsertAndConnect?: (node: AnimationGraphNode, connection: AnimationConnection) => void;
  onUpdateNode: (node: AnimationGraphNode, label?: string) => void;
  onUpdateVisualGroup: (group: AnimationGraphVisualGroup, label?: string) => void;
  onDeleteVisualGroup: (groupId: string) => void;
  onAddAnimateProperty: (nodeId: string) => void;
  onPlay: (rootNodeId: string) => void;
  onPause: () => void;
  onWheelGesture: (event: WheelEvent<Element>) => void;
  visibleViewportInsets: ViewportScreenInsets;
  onAutopanScreenDelta: (delta: { x: number; y: number }) => void;
}

type AnimationNodeGhost = { point: { x: number; y: number }; kind: AnimationNodeKind };

export interface AnimationGraphOverlayHandle {
  setCamera: (camera: Camera) => void;
}

const NODE_WIDTH = 220;
const COLLAPSED_NODE_HEIGHT = 36;
const PIN_RADIUS = 7;
const VISUAL_GROUP_PADDING = 16;

type AnimatablePropertyKind =
  | "opacity"
  | "position"
  | "scale"
  | "scaleX"
  | "scaleY"
  | "rotation"
  | "width"
  | "height"
  | "layoutX"
  | "layoutY"
  | "stack"
  | "inline"
  | "fitToBBox"
  | "followPath"
  | "strokePath"
  | "orbit"
  | "lookAt"
  | "cornerRadius"
  | "strokeWidth"
  | "fill"
  | "stroke"
  | "effect";

const ANIMATABLE_PROPERTY_OPTIONS: Array<{ kind: AnimatablePropertyKind; label: string }> = [
  { kind: "opacity", label: "Opacity" },
  { kind: "fill", label: "Fill" },
  { kind: "stroke", label: "Stroke" },
  { kind: "effect", label: "Effect" },
  { kind: "position", label: "Position" },
  { kind: "scale", label: "Scale" },
  { kind: "scaleX", label: "Scale X" },
  { kind: "scaleY", label: "Scale Y" },
  { kind: "rotation", label: "Rotation" },
  { kind: "width", label: "Width" },
  { kind: "height", label: "Height" },
  { kind: "cornerRadius", label: "Corner Radius" },
  { kind: "strokeWidth", label: "Stroke Width" },
  { kind: "layoutX", label: "Layout X" },
  { kind: "layoutY", label: "Layout Y" },
  { kind: "stack", label: "Stack" },
  { kind: "inline", label: "Inline" },
  { kind: "fitToBBox", label: "Fit to BBox" },
  { kind: "followPath", label: "Follow path" },
  { kind: "strokePath", label: "Stroke path" },
  { kind: "orbit", label: "Orbit" },
  { kind: "lookAt", label: "Look At" },
];

const COLOR_SPACE_OPTIONS: Array<{ value: ColorAnimationSpace; label: string }> = [
  { value: "oklch", label: "OKLCH" },
  { value: "hsl", label: "HSL" },
  { value: "rgb", label: "RGB" },
  { value: "hsb", label: "HSB" },
];

const GROUP_REGROUP_OPTIONS: Array<{ value: AnimationGroupRegroupMode; label: string }> = [
  { value: "none", label: "None" },
  { value: "words", label: "Regroup as words" },
  { value: "symbols", label: "Regroup as symbols" },
  { value: "symbolsWithoutWhitespace", label: "Regroup as symbols without whitespaces" },
];
const GROUP_TARGET_DRAG_MIME = "application/x-curva-animation-group-target-index";

const EFFECT_FIELD_OPTIONS: Array<{ value: EffectAnimationField | "color"; label: string }> = [
  { value: "blur", label: "Blur" },
  { value: "spread", label: "Spread" },
  { value: "offsetX", label: "Offset X" },
  { value: "offsetY", label: "Offset Y" },
  { value: "opacity", label: "Opacity" },
  { value: "color", label: "Color" },
];

const ANIMATION_ANCHOR_OPTIONS: Array<{ anchor: AnimationAnchor; label: string }> = [
  { anchor: "origin", label: "Origin" },
  { anchor: "topLeft", label: "Top left" },
  { anchor: "top", label: "Top" },
  { anchor: "topRight", label: "Top right" },
  { anchor: "left", label: "Left" },
  { anchor: "center", label: "Center" },
  { anchor: "right", label: "Right" },
  { anchor: "bottomLeft", label: "Bottom left" },
  { anchor: "bottom", label: "Bottom" },
  { anchor: "bottomRight", label: "Bottom right" },
  { anchor: "baselineLeft", label: "Baseline left" },
  { anchor: "baseline", label: "Baseline" },
];

type PinTargetOption = "point" | "offset" | "distance" | "minDistance" | "maxDistance" | "pathPercent" | `frame:${AnimationAnchor}`;

const PIN_TARGET_OPTIONS: Array<{ value: PinTargetOption; label: string }> = [
  { value: "offset", label: "Offset" },
  { value: "distance", label: "Distance" },
  { value: "minDistance", label: "Min distance" },
  { value: "maxDistance", label: "Max distance" },
  { value: "point", label: "Point" },
  ...ANIMATION_ANCHOR_OPTIONS.map(option => ({ value: `frame:${option.anchor}` as const, label: titleCaseAnchorLabel(option.label) })),
  { value: "pathPercent", label: "Path percent" },
];

const NO_FRAME_VALUE = "__none_frame__";
const nodeSelectTriggerClass = "h-7 shadow-none " + panelSelectTriggerClass;
const nodeSelectContentClass = "z-[60] " + panelSelectContentClass;
const nodeSelectContentPosition = "popper" as const;
type MotionEasingOption = `preset:${AnimationEasingName}` | "wiggle";
const MOTION_EASING_OPTIONS: Array<{ value: MotionEasingOption; label: string }> = [
  ...ANIMATION_EASING_NAMES.map(name => ({ value: `preset:${name}` as const, label: name })),
  { value: "wiggle", label: "Wiggle" },
];

function titleCaseAnchorLabel(label: string): string {
  return label.replace(/\b\w/g, char => char.toUpperCase());
}

type DragState =
  | { kind: "nodes"; pointerId: number; start: { x: number; y: number }; before: Array<{ nodeId: string; position: { x: number; y: number } }>; moved: boolean }
  | { kind: "visualGroup"; pointerId: number; groupId: string; start: { x: number; y: number }; before: Array<{ nodeId: string; position: { x: number; y: number } }>; moved: boolean }
  | { kind: "marquee"; pointerId: number; start: { x: number; y: number }; current: { x: number; y: number } }
  | { kind: "connection"; pointerId: number; from: AnimationEndpoint; current: { x: number; y: number } }
  | { kind: "objectConnection"; pointerId: number; endpoint: AnimationEndpoint; current: { x: number; y: number }; deepSelect: boolean };

type ObjectPickerState = {
  endpoint: AnimationEndpoint;
  position: { x: number; y: number };
};

type AnimationPickerState = {
  position: { x: number; y: number };
};

const NON_SCALING_STROKE_STYLE = { vectorEffect: "non-scaling-stroke" } as const;
const VISUAL_GROUP_LABEL_OFFSET_PX = 26;
const TITLE_DOUBLE_PRESS_MS = 360;
const TITLE_DOUBLE_PRESS_DISTANCE_PX = 5;
const CONNECTION_AUTOPAN_MAX_STEP_PX = 18;
const AUTOPAN_EDGE_ZONE_PX = 10;

interface TitlePressState {
  id: string;
  time: number;
  x: number;
  y: number;
}

function isTitleDoublePress(ref: { current: TitlePressState | undefined }, id: string, event: PointerEvent<Element>): boolean {
  const now = performance.now();
  const previous = ref.current;
  const isDouble = Boolean(
    previous &&
    previous.id === id &&
    now - previous.time <= TITLE_DOUBLE_PRESS_MS &&
    Math.hypot(event.clientX - previous.x, event.clientY - previous.y) <= TITLE_DOUBLE_PRESS_DISTANCE_PX,
  );
  ref.current = isDouble ? undefined : { id, time: now, x: event.clientX, y: event.clientY };
  return isDouble;
}

function connectionAutopanDelta(
  pointer: { clientX: number; clientY: number },
  rect: DOMRect,
  insets: ViewportScreenInsets,
): { x: number; y: number } {
  const visible = {
    left: rect.left + insets.left,
    top: rect.top + insets.top,
    right: rect.right - insets.right,
    bottom: rect.bottom - insets.bottom,
  };
  const width = Math.max(1, visible.right - visible.left);
  const height = Math.max(1, visible.bottom - visible.top);
  return {
    x: edgeAutopanAxis(pointer.clientX, visible.left, visible.right, Math.min(AUTOPAN_EDGE_ZONE_PX, width / 2)),
    y: edgeAutopanAxis(pointer.clientY, visible.top, visible.bottom, Math.min(AUTOPAN_EDGE_ZONE_PX, height / 2)),
  };
}

function edgeAutopanAxis(value: number, min: number, max: number, band: number): number {
  const clampedBand = Math.max(1, band);
  if (value < min + clampedBand) {
    return -CONNECTION_AUTOPAN_MAX_STEP_PX * Math.min(1, (min + clampedBand - value) / clampedBand);
  }
  if (value > max - clampedBand) {
    return CONNECTION_AUTOPAN_MAX_STEP_PX * Math.min(1, (value - (max - clampedBand)) / clampedBand);
  }
  return 0;
}

function setCameraTransform(element: HTMLElement | SVGElement | null, camera: Camera): void {
  if (!element) {
    return;
  }
  element.style.transform = `translate(${-camera.x * camera.zoom}px, ${-camera.y * camera.zoom}px) scale(${camera.zoom})`;
  element.style.setProperty("--animation-graph-inverse-zoom", String(1 / camera.zoom));
  element.style.setProperty("--animation-graph-label-offset", `${VISUAL_GROUP_LABEL_OFFSET_PX / camera.zoom}px`);
}

const AnimationGraphOverlayImpl = forwardRef<AnimationGraphOverlayHandle, AnimationGraphOverlayProps>(function AnimationGraphOverlay({
  graph,
  document,
  camera,
  active,
  selectedNodeIds,
  selectedConnectionIds,
  selectedObjectConnectionKeys,
  objectDragGhost,
  frames,
  playing,
  onSelectNodes,
  onSelectConnections,
  onSelectObjectConnections,
  onMoveNodes,
  onDuplicateNodes,
  onConnect,
  onObjectConnect,
  onInsertAndConnect,
  onUpdateNode,
  onUpdateVisualGroup,
  onDeleteVisualGroup,
  onAddAnimateProperty,
  onPlay,
  onPause,
  onWheelGesture,
  visibleViewportInsets,
  onAutopanScreenDelta,
}, ref) {
  const [drag, setDrag] = useState<DragState | undefined>(undefined);
  const [previewPositions, setPreviewPositions] = useState<Map<string, { x: number; y: number }> | undefined>(undefined);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | undefined>(undefined);
  const [connectionHoverPin, setConnectionHoverPin] = useState<AnimationEndpoint | undefined>(undefined);
  const [objectPicker, setObjectPicker] = useState<ObjectPickerState | undefined>(undefined);
  const [renamingGroupId, setRenamingGroupId] = useState<string | undefined>(undefined);
  const [renamingGroupName, setRenamingGroupName] = useState("");
  const [bypassConnectionHitTargets, setBypassConnectionHitTargets] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const worldRef = useRef<HTMLDivElement | null>(null);
  const cameraRef = useRef(camera);
  const visualGroupTitlePressRef = useRef<TitlePressState | undefined>(undefined);
  const dragStateRef = useRef<DragState | undefined>(undefined);
  const dragPointerRef = useRef<{ clientX: number; clientY: number; pointerId: number } | undefined>(undefined);
  const autopanFrameRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    dragStateRef.current = drag;
  }, [drag]);

  useImperativeHandle(ref, () => ({
    setCamera(nextCamera) {
      cameraRef.current = nextCamera;
      setCameraTransform(worldRef.current, nextCamera);
    },
  }), []);

  useLayoutEffect(() => {
    cameraRef.current = camera;
    setCameraTransform(worldRef.current, camera);
  }, [active, camera]);

  useEffect(() => () => stopGraphDragAutopan(), []);

  useEffect(() => {
    if (!active) {
      setBypassConnectionHitTargets(false);
      return;
    }

    const syncModifierState = (event: globalThis.KeyboardEvent) => {
      setBypassConnectionHitTargets(deepSelectModifier(event));
    };
    const clearModifierState = () => setBypassConnectionHitTargets(false);

    window.addEventListener("keydown", syncModifierState);
    window.addEventListener("keyup", syncModifierState);
    window.addEventListener("blur", clearModifierState);
    return () => {
      window.removeEventListener("keydown", syncModifierState);
      window.removeEventListener("keyup", syncModifierState);
      window.removeEventListener("blur", clearModifierState);
    };
  }, [active]);

  useEffect(() => {
    if (!active) {
      stopGraphDragAutopan();
      setObjectPicker(undefined);
    }
  }, [active]);

  const nodeById = useMemo(() => new Map(graph.nodes.map(node => [node.id, node] as const)), [graph.nodes]);

  // BFS to map each node to its animation root id.
  const nodeRootMap = useMemo(() => {
    const result = new Map<string, string>();
    for (const root of graph.nodes.filter(n => n.kind === "animation")) {
      const visited = new Set<string>();
      const queue = [root.id];
      while (queue.length > 0) {
        const id = queue.shift()!;
        if (visited.has(id)) continue;
        visited.add(id);
        result.set(id, root.id);
        for (const conn of graph.connections) {
          if (conn.from.nodeId === id && !visited.has(conn.to.nodeId)) queue.push(conn.to.nodeId);
          if (conn.to.nodeId === id && !visited.has(conn.from.nodeId)) queue.push(conn.from.nodeId);
        }
      }
    }
    return result;
  }, [graph]);

  const focusNodeId = selectedNodeIds[0] ?? hoveredNodeId;
  const focusRootId = focusNodeId ? nodeRootMap.get(focusNodeId) : undefined;
  const visualGroups = graph.visualGroups ?? [];
  const collapsedNodeIds = useMemo(() => {
    const result = new Set<string>();
    for (const group of visualGroups) {
      if (!group.collapsed) continue;
      for (const nodeId of group.nodeIds) {
        result.add(nodeId);
      }
    }
    return result;
  }, [visualGroups]);
  const visualGroupLayouts = useMemo(() => visualGroups.flatMap(group => {
    const rects = group.nodeIds.flatMap(nodeId => {
      const node = nodeById.get(nodeId);
      return node ? [visibleNodeRect(node, previewPositions, graph, document)] : [];
    });
    if (rects.length === 0) {
      return [];
    }
    const memberBounds = unionRects(rects);
    return [{
      group,
      memberBounds,
      bounds: {
        x: memberBounds.x - VISUAL_GROUP_PADDING,
        y: memberBounds.y - VISUAL_GROUP_PADDING,
        width: memberBounds.width + VISUAL_GROUP_PADDING * 2,
        height: memberBounds.height + VISUAL_GROUP_PADDING * 2,
      },
    }];
  }), [document, graph, nodeById, previewPositions, visualGroups]);
  const collapsedVisualGroupByNodeId = useMemo(() => {
    const result = new Map<string, { groupId: string; bounds: { x: number; y: number; width: number; height: number } }>();
    for (const { group, bounds } of visualGroupLayouts) {
      if (!group.collapsed) continue;
      for (const nodeId of group.nodeIds) {
        result.set(nodeId, { groupId: group.id, bounds });
      }
    }
    return result;
  }, [visualGroupLayouts]);

  function nodeConnOpacity(fromNodeId: string, toNodeId: string): "normal" | "dimmed" | "hidden" {
    if (!focusRootId) return "normal";
    const connRoot = nodeRootMap.get(fromNodeId) ?? nodeRootMap.get(toNodeId);
    if (connRoot !== focusRootId) return "hidden";
    const focusIds = selectedNodeIds.length > 0 ? selectedNodeIds : hoveredNodeId ? [hoveredNodeId] : [];
    if (focusIds.length > 0) {
      const direct = focusIds.some(id => id === fromNodeId || id === toNodeId);
      if (!direct) return "dimmed";
    }
    return "normal";
  }

  function selectAnimationFlow(rootNodeId: string): void {
    const flowNodeIds = graph.nodes
      .filter(node => nodeRootMap.get(node.id) === rootNodeId)
      .map(node => node.id);
    onSelectNodes(flowNodeIds, rootNodeId);
    onSelectConnections([]);
    onSelectObjectConnections([]);
  }

  const connectionPaths = graph.connections.flatMap(connection => {
    const fromNode = nodeById.get(connection.from.nodeId);
    const toNode = nodeById.get(connection.to.nodeId);
    if (!fromNode || !toNode) {
      return [];
    }
    const fromCollapsedGroup = collapsedVisualGroupByNodeId.get(fromNode.id);
    const toCollapsedGroup = collapsedVisualGroupByNodeId.get(toNode.id);
    if (fromCollapsedGroup && toCollapsedGroup && fromCollapsedGroup.groupId === toCollapsedGroup.groupId) {
      return [];
    }
    const fromDir = pinDir(connection.from.pin, fromNode.kind);
    const toDir = pinDir(connection.to.pin, toNode.kind);
    const from = fromCollapsedGroup
      ? visualGroupBadgePoint(fromCollapsedGroup.bounds, fromDir)
      : pinPoint(fromNode, connection.from, previewPositions, graph, document);
    const to = toCollapsedGroup
      ? visualGroupBadgePoint(toCollapsedGroup.bounds, toDir)
      : pinPoint(toNode, connection.to, previewPositions, graph, document);
    return [{
      connection,
      from,
      to,
      fromDir,
      toDir,
    }];
  });
  const objectConnectionPaths = useMemo(() => {
    return animationObjectLinks(graph).flatMap(link => {
      const target = link.target;
      const sourceNode = findSceneNode(document, target.nodeId);
      const toNode = nodeById.get(link.endpoint.nodeId);
      if (!sourceNode || !toNode) {
        return [];
      }
      const collapsedGroup = collapsedVisualGroupByNodeId.get(toNode.id);
      const targetKey = animationTargetKey(target);
      const fromPoint = vectorSubElementPoint(document, target, sourceNode) ?? nodeCenter(sourceNode);
      return [{
        key: `${targetKey}:${link.endpoint.nodeId}:${link.endpoint.pin}:${link.endpoint.index ?? 0}`,
        animNodeId: link.endpoint.nodeId,
        dependency: link.endpoint.pin === "dependencyInput",
        from: fromPoint,
        to: collapsedGroup ? visualGroupBadgePoint(collapsedGroup.bounds, pinDir(link.endpoint.pin, toNode.kind)) : pinPoint(toNode, link.endpoint, previewPositions, graph, document),
      }];
    });
  }, [collapsedVisualGroupByNodeId, document, graph, nodeById, previewPositions]);

  if (!active) {
    return null;
  }

  function clientToDocument(clientX: number, clientY: number): { x: number; y: number } {
    const rect = rootRef.current?.getBoundingClientRect();
    const activeCamera = cameraRef.current;
    return {
      x: activeCamera.x + (clientX - (rect?.left ?? 0)) / activeCamera.zoom,
      y: activeCamera.y + (clientY - (rect?.top ?? 0)) / activeCamera.zoom,
    };
  }

  function startGraphDragAutopan(): void {
    if (autopanFrameRef.current !== undefined) {
      return;
    }
    const tick = () => {
      autopanFrameRef.current = undefined;
      const pointer = dragPointerRef.current;
      const activeDrag = dragStateRef.current;
      const root = rootRef.current;
      if (!pointer || !activeDrag || !root) {
        return;
      }

      const delta = connectionAutopanDelta(pointer, root.getBoundingClientRect(), visibleViewportInsets);
      if (delta.x !== 0 || delta.y !== 0) {
        onAutopanScreenDelta(delta);
        const current = clientToDocument(pointer.clientX, pointer.clientY);
        if (activeDrag.kind === "nodes" || activeDrag.kind === "visualGroup") {
          const dx = current.x - activeDrag.start.x;
          const dy = current.y - activeDrag.start.y;
          setPreviewPositions(new Map(activeDrag.before.map(item => [item.nodeId, { x: item.position.x + dx, y: item.position.y + dy }])));
          setDrag(previous => {
            if (!previous || previous.pointerId !== pointer.pointerId || (previous.kind !== "nodes" && previous.kind !== "visualGroup")) {
              return previous;
            }
            const next = { ...previous, moved: previous.moved || Math.hypot(dx, dy) > 1 };
            dragStateRef.current = next;
            return next;
          });
        } else if (activeDrag.kind === "connection") {
          const hover = nearestInputPin(graph, current, activeDrag.from.nodeId, previewPositions, document);
          setConnectionHoverPin(hover);
          setDrag(previous => {
            if (!previous || previous.kind !== "connection" || previous.pointerId !== pointer.pointerId) {
              return previous;
            }
            const next = { ...previous, current };
            dragStateRef.current = next;
            return next;
          });
        } else if (activeDrag.kind === "objectConnection") {
          setDrag(previous => {
            if (!previous || previous.kind !== "objectConnection" || previous.pointerId !== pointer.pointerId) {
              return previous;
            }
            const next = { ...previous, current };
            dragStateRef.current = next;
            return next;
          });
        }
      }

      autopanFrameRef.current = window.requestAnimationFrame(tick);
    };
    autopanFrameRef.current = window.requestAnimationFrame(tick);
  }

  function stopGraphDragAutopan(): void {
    dragPointerRef.current = undefined;
    if (autopanFrameRef.current !== undefined) {
      window.cancelAnimationFrame(autopanFrameRef.current);
      autopanFrameRef.current = undefined;
    }
  }

  function handleNodePointerDown(node: AnimationGraphNode, event: PointerEvent<HTMLDivElement>): void {
    if (event.button !== 0) {
      return;
    }
    const nextSelection = event.shiftKey
      ? toggle(selectedNodeIds, node.id)
      : selectedNodeIds.includes(node.id)
        ? selectedNodeIds
        : [node.id];
    onSelectNodes(nextSelection, node.id);
    event.preventDefault();
    event.stopPropagation();
    const selected = new Set(nextSelection);
    const before = graph.nodes
      .filter(candidate => selected.has(candidate.id))
      .map(candidate => ({ nodeId: candidate.id, position: { ...candidate.position } }));
    event.currentTarget.setPointerCapture(event.pointerId);
    dragPointerRef.current = { clientX: event.clientX, clientY: event.clientY, pointerId: event.pointerId };
    const nextDrag: DragState = { kind: "nodes", pointerId: event.pointerId, start: clientToDocument(event.clientX, event.clientY), before, moved: false };
    dragStateRef.current = nextDrag;
    setDrag(nextDrag);
    startGraphDragAutopan();
  }

  function handleVisualGroupPointerDown(group: AnimationGraphVisualGroup, event: PointerEvent<HTMLDivElement>, options?: { preventDefault?: boolean }): void {
    if (event.button !== 0) {
      return;
    }
    const firstAnimationNode = group.nodeIds
      .map(nodeId => nodeById.get(nodeId))
      .find(node => node?.kind === "animation");
    if (firstAnimationNode) {
      onSelectNodes([firstAnimationNode.id], firstAnimationNode.id);
      onSelectConnections([]);
      onSelectObjectConnections([]);
    }
    if (options?.preventDefault ?? true) {
      event.preventDefault();
    }
    event.stopPropagation();
    const nodeIds = new Set(group.nodeIds);
    const before = graph.nodes
      .filter(candidate => nodeIds.has(candidate.id))
      .map(candidate => ({ nodeId: candidate.id, position: { ...candidate.position } }));
    if (before.length === 0) {
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    dragPointerRef.current = { clientX: event.clientX, clientY: event.clientY, pointerId: event.pointerId };
    const nextDrag: DragState = { kind: "visualGroup", pointerId: event.pointerId, groupId: group.id, start: clientToDocument(event.clientX, event.clientY), before, moved: false };
    dragStateRef.current = nextDrag;
    setDrag(nextDrag);
    startGraphDragAutopan();
  }

  function startVisualGroupRename(group: AnimationGraphVisualGroup): void {
    setRenamingGroupId(group.id);
    setRenamingGroupName(group.name);
  }

  function commitVisualGroupRename(group: AnimationGraphVisualGroup): void {
    const nextName = renamingGroupName.trim();
    setRenamingGroupId(undefined);
    if (nextName.length === 0 || nextName === group.name) {
      setRenamingGroupName("");
      return;
    }
    setRenamingGroupName("");
    onUpdateVisualGroup({ ...group, name: nextName }, "Renamed animation group");
  }

  function handleVisualGroupRenameKeyDown(group: AnimationGraphVisualGroup, event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "Enter") {
      event.preventDefault();
      commitVisualGroupRename(group);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setRenamingGroupId(undefined);
      setRenamingGroupName("");
    }
  }

  function handlePinPointerDown(node: AnimationGraphNode, pin: AnimationPinKind, event: PointerEvent<HTMLButtonElement>, index?: number): void {
    if (event.button !== 0) {
      return;
    }
    const endpoint = { nodeId: node.id, pin, index };
    if (!isOutputPin(pin) && (!onObjectConnect || !objectInputPinsForNode(node).includes(pin))) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragPointerRef.current = { clientX: event.clientX, clientY: event.clientY, pointerId: event.pointerId };
    const current = clientToDocument(event.clientX, event.clientY);
    const nextDrag: DragState = isOutputPin(pin)
      ? {
          kind: "connection",
          pointerId: event.pointerId,
          from: endpoint,
          current,
        }
      : {
          kind: "objectConnection",
          pointerId: event.pointerId,
          endpoint,
          current,
          deepSelect: deepSelectModifier(event),
        };
    dragStateRef.current = nextDrag;
    setDrag(nextDrag);
    startGraphDragAutopan();
  }

  function handlePinContextMenu(node: AnimationGraphNode, pin: AnimationPinKind, event: MouseEvent<HTMLButtonElement>, index?: number): void {
    if (!onObjectConnect || !objectInputPinsForNode(node).includes(pin)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    setObjectPicker({
      endpoint: { nodeId: node.id, pin, index },
      position: { x: event.clientX, y: event.clientY },
    });
  }

  function handlePointerMove(event: PointerEvent<HTMLDivElement>): void {
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    const point = clientToDocument(event.clientX, event.clientY);
    if (drag.kind === "nodes" || drag.kind === "visualGroup" || drag.kind === "connection" || drag.kind === "objectConnection") {
      dragPointerRef.current = { clientX: event.clientX, clientY: event.clientY, pointerId: event.pointerId };
    }
    if (drag.kind === "nodes") {
      const dx = point.x - drag.start.x;
      const dy = point.y - drag.start.y;
      setPreviewPositions(new Map(drag.before.map(item => [item.nodeId, { x: item.position.x + dx, y: item.position.y + dy }])));
      const nextDrag = { ...drag, moved: drag.moved || Math.hypot(dx, dy) > 1 };
      dragStateRef.current = nextDrag;
      setDrag(nextDrag);
      return;
    }
    if (drag.kind === "visualGroup") {
      const dx = point.x - drag.start.x;
      const dy = point.y - drag.start.y;
      setPreviewPositions(new Map(drag.before.map(item => [item.nodeId, { x: item.position.x + dx, y: item.position.y + dy }])));
      const nextDrag = { ...drag, moved: drag.moved || Math.hypot(dx, dy) > 1 };
      dragStateRef.current = nextDrag;
      setDrag(nextDrag);
      return;
    }
    if (drag.kind === "marquee") {
      setDrag({ ...drag, current: point });
      return;
    }
    if (drag.kind === "connection") {
      const nearest = nearestInputPin(graph, point, drag.from.nodeId, previewPositions, document);
      setConnectionHoverPin(nearest);
    }
    if (drag.kind === "objectConnection") {
      const nextDrag = { ...drag, current: point, deepSelect: deepSelectModifier(event) };
      dragStateRef.current = nextDrag;
      setDrag(nextDrag);
      return;
    }
    const nextDrag = { ...drag, current: point };
    dragStateRef.current = nextDrag;
    setDrag(nextDrag);
  }

  function handlePointerUp(event: PointerEvent<HTMLDivElement>): void {
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    event.stopPropagation();
    if (drag.kind === "nodes" || drag.kind === "visualGroup") {
      const after = drag.before.flatMap(item => {
        const position = previewPositions?.get(item.nodeId);
        return position ? [{ nodeId: item.nodeId, position }] : [];
      });
      if (after.length > 0 && drag.moved) {
        if (drag.kind === "nodes" && event.altKey) {
          onDuplicateNodes(after);
        } else {
          onMoveNodes(drag.before, after);
        }
      }
      setPreviewPositions(undefined);
    } else if (drag.kind === "marquee") {
      const rect = normalizedRect(drag.start, drag.current);
      const selected = graph.nodes
        .filter(node => !collapsedNodeIds.has(node.id) && rectIntersects(rect, nodeRect(node, previewPositions, graph, document)))
        .map(node => node.id);
      onSelectNodes(event.shiftKey ? merge(selectedNodeIds, selected) : selected, selected.at(-1));
    } else if (drag.kind === "connection") {
      const point = clientToDocument(event.clientX, event.clientY);
      const target = nearestInputPin(graph, point, drag.from.nodeId, previewPositions, document);
      if (target) {
        onConnect({
          id: createId("animConn"),
          from: drag.from,
          to: target,
        });
        onSelectNodes([target.nodeId], target.nodeId);
      } else if (onInsertAndConnect) {
        const sourceNode = nodeById.get(drag.from.nodeId);
        if (sourceNode?.kind === "group" && drag.from.pin === "output") {
          const newNode = createAnimationGraphNode("animate", point);
          onInsertAndConnect(newNode, {
            id: createId("animConn"),
            from: drag.from,
            to: { nodeId: newNode.id, pin: "input" },
          });
        } else if (sourceNode && canDragCreateMotionNode(sourceNode, drag.from.pin)) {
          const sequenceOutput = isMotionSequenceOutput(sourceNode, drag.from.pin);
          const newNode = createAnimationGraphNode("motion", sequenceOutput ? topCenterNodePosition(point) : point);
          const toPin: AnimationPinKind = sequenceOutput ? "motionSequenceInput" : "motionInput";
          onInsertAndConnect(newNode, {
            id: createId("animConn"),
            from: drag.from,
            to: { nodeId: newNode.id, pin: toPin },
          });
        }
      }
    } else if (drag.kind === "objectConnection") {
      const point = clientToDocument(event.clientX, event.clientY);
      const hit = hitTestAnimationObject(document, point, cameraRef.current.zoom, deepSelectModifier(event) || drag.deepSelect);
      if (hit && onObjectConnect) {
        onObjectConnect(hit, drag.endpoint);
        onSelectNodes([drag.endpoint.nodeId], drag.endpoint.nodeId);
        onSelectConnections([]);
        onSelectObjectConnections([]);
      }
    }
    setDrag(undefined);
    dragStateRef.current = undefined;
    setConnectionHoverPin(undefined);
    stopGraphDragAutopan();
  }

  const marquee = drag?.kind === "marquee" ? normalizedRect(drag.start, drag.current) : undefined;

  const connectionDragGhost = drag?.kind === "connection" && !connectionHoverPin
    ? ((): AnimationNodeGhost | undefined => {
        const sourceNode = nodeById.get(drag.from.nodeId);
        if (!sourceNode) return undefined;
        if (sourceNode.kind === "group" && drag.from.pin === "output") return { point: drag.current, kind: "animate" as const };
        if (canDragCreateMotionNode(sourceNode, drag.from.pin)) {
          const point = isMotionSequenceOutput(sourceNode, drag.from.pin) ? topCenterNodePosition(drag.current) : drag.current;
          return { point, kind: "motion" as const };
        }
        return undefined;
      })()
    : undefined;

  const connectionPreview = drag?.kind === "connection"
    ? (() => {
        const fromNode = nodeById.get(drag.from.nodeId)!;
        return {
          from: pinPoint(fromNode, drag.from, previewPositions, graph, document),
          to: drag.current,
          fromDir: pinDir(drag.from.pin, fromNode.kind),
          dependency: connectionHoverPin?.pin === "dependencyInput",
        };
      })()
    : undefined;

  const objectConnectionPreview = drag?.kind === "objectConnection"
    ? (() => {
        const fromNode = nodeById.get(drag.endpoint.nodeId);
        if (!fromNode) return undefined;
        return {
          from: pinPoint(fromNode, drag.endpoint, previewPositions, graph, document),
          to: drag.current,
          fromDir: pinDir(drag.endpoint.pin, fromNode.kind),
          color: drag.endpoint.pin === "dependencyInput" ? "dependency" as const : "object" as const,
        };
      })()
    : undefined;

  return (
    <div
      ref={rootRef}
      className="pointer-events-none absolute inset-0 z-[8] overflow-hidden"
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => {
        setDrag(undefined);
        dragStateRef.current = undefined;
        setPreviewPositions(undefined);
        setConnectionHoverPin(undefined);
        stopGraphDragAutopan();
      }}
    >
      <div ref={worldRef} className="absolute left-0 top-0 origin-top-left will-change-transform">
        {visualGroupLayouts.map(({ group, bounds }) => (
          <VisualGroupFrame
            key={group.id}
            group={group}
            bounds={bounds}
            renaming={renamingGroupId === group.id}
            renameValue={renamingGroupName}
            onRenameValueChange={setRenamingGroupName}
            onPointerDown={event => handleVisualGroupPointerDown(group, event)}
            onTitlePointerDown={event => {
              if (isTitleDoublePress(visualGroupTitlePressRef, group.id, event)) {
                event.preventDefault();
                event.stopPropagation();
                startVisualGroupRename(group);
                return;
              }
              handleVisualGroupPointerDown(group, event, { preventDefault: false });
            }}
            onRenameStart={() => startVisualGroupRename(group)}
            onRenameCommit={() => commitVisualGroupRename(group)}
            onRenameKeyDown={event => handleVisualGroupRenameKeyDown(group, event)}
            onDelete={() => onDeleteVisualGroup(group.id)}
            onToggleCollapsed={() => onUpdateVisualGroup({ ...group, collapsed: !group.collapsed }, group.collapsed ? "Expanded animation group" : "Collapsed animation group")}
            onWheel={onWheelGesture}
          />
        ))}
        <svg className="pointer-events-none absolute left-0 top-0 h-px w-px overflow-visible">
          {!playing && objectConnectionPaths.map(({ key, from, to, animNodeId, dependency }) => {
            const selected = selectedObjectConnectionKeys.includes(key);
            const opacity = nodeConnOpacity(animNodeId, animNodeId);
            if (opacity === "hidden") return null;
            const hitTargetClass = bypassConnectionHitTargets ? "pointer-events-none" : "pointer-events-auto cursor-pointer";
            return (
              <g key={key} className={`transition-opacity duration-100 ${opacity === "dimmed" ? "opacity-30" : "opacity-100"}`}>
                <path
                  d={curvePath(from, to)}
                  className={`${hitTargetClass} fill-none stroke-transparent stroke-[12]`}
                  style={NON_SCALING_STROKE_STYLE}
                  onWheel={onWheelGesture}
                  onPointerDown={event => {
                    event.stopPropagation();
                    onSelectNodes([], undefined);
                    onSelectConnections([]);
                    onSelectObjectConnections([key]);
                  }}
                />
                <path
                  d={curvePath(from, to)}
                  className={`pointer-events-none fill-none stroke-[2] ${connectionStrokeClass(selected, dependency, "object")}`}
                  style={NON_SCALING_STROKE_STYLE}
                />
              </g>
            );
          })}
          {connectionPaths.map(({ connection, from, to, fromDir, toDir }) => {
            const opacity = nodeConnOpacity(connection.from.nodeId, connection.to.nodeId);
            if (opacity === "hidden") return null;
            const hitTargetClass = bypassConnectionHitTargets ? "pointer-events-none" : "pointer-events-auto cursor-pointer";
            return (
              <g key={connection.id} className={`transition-opacity duration-100 ${opacity === "dimmed" ? "opacity-30" : "opacity-100"}`}>
                <path
                  d={curvePath(from, to, fromDir, toDir)}
                  className={`${hitTargetClass} fill-none stroke-transparent stroke-[12]`}
                  style={NON_SCALING_STROKE_STYLE}
                  onWheel={onWheelGesture}
                  onPointerDown={event => {
                    event.stopPropagation();
                    onSelectNodes([], undefined);
                    onSelectConnections([connection.id]);
                    onSelectObjectConnections([]);
                  }}
                />
                <path
                  d={curvePath(from, to, fromDir, toDir)}
                  className={`pointer-events-none fill-none stroke-[2] ${connectionStrokeClass(selectedConnectionIds.includes(connection.id), connectionColorForGraphConnection(connection, nodeById), "graph")}`}
                  style={NON_SCALING_STROKE_STYLE}
                />
              </g>
            );
          })}
          {connectionPreview ? <path d={curvePath(connectionPreview.from, connectionPreview.to, connectionPreview.fromDir)} className={`fill-none stroke-[2] opacity-80 ${previewConnectionStrokeClass(drag?.kind === "connection" ? connectionColorForEndpoint(drag.from, nodeById.get(drag.from.nodeId)) : "default")}`} style={NON_SCALING_STROKE_STYLE} /> : null}
          {objectConnectionPreview ? <path d={curvePath(objectConnectionPreview.from, objectConnectionPreview.to, objectConnectionPreview.fromDir)} className={`fill-none stroke-[2] opacity-80 ${previewConnectionStrokeClass(objectConnectionPreview.color)}`} style={NON_SCALING_STROKE_STYLE} /> : null}
          {marquee ? (
            <rect
              x={marquee.x}
              y={marquee.y}
              width={marquee.width}
              height={marquee.height}
              className="fill-sky-400/10 stroke-sky-400/70"
              style={NON_SCALING_STROKE_STYLE}
            />
          ) : null}
        </svg>
        {graph.nodes.filter(node => !collapsedNodeIds.has(node.id)).map(node => {
          const position = previewPositions?.get(node.id) ?? node.position;
          const selected = selectedNodeIds.includes(node.id);
          return (
            <div
              key={node.id}
              className={`pointer-events-auto absolute origin-top-left rounded border bg-[#101318]/95 text-slate-100 shadow-lg shadow-black/30 ${selected ? "border-sky-400 ring-1 ring-sky-400/50" : "border-slate-600"}`}
              style={{ width: NODE_WIDTH, minHeight: nodeHeight(node, graph, document), transform: `translate(${position.x}px, ${position.y}px)` }}
              onWheel={onWheelGesture}
              onPointerDown={event => handleNodePointerDown(node, event)}
              onMouseEnter={() => setHoveredNodeId(node.id)}
              onMouseLeave={() => setHoveredNodeId(undefined)}
            >
              <NodeContent
                node={node}
                graph={graph}
                document={document}
                frames={frames}
                playing={playing}
                onUpdateNode={onUpdateNode}
                onAddAnimateProperty={() => onAddAnimateProperty(node.id)}
                onPlay={() => onPlay(node.id)}
                onPause={onPause}
                onSelectFlow={() => selectAnimationFlow(node.id)}
                onFocus={() => {
                  onSelectNodes([node.id], node.id);
                  onSelectConnections([]);
                }}
              />
              {pinsForNode(node, graph).map(pin => {
                if (!isPinVisibleOnNode(node, pin.pin)) {
                  return null;
                }
                const point = localPinPoint(node, { nodeId: node.id, pin: pin.pin, index: pin.index }, graph, document);
                const isAnimationOutput = node.kind === "animation" && pin.pin === "output";
                const isBottom = pin.pin === "motionSequenceOutput" || isAnimationOutput;
                const isSequencePin = pin.pin === "motionSequenceInput" || pin.pin === "motionSequenceOutput" || isAnimationOutput;
                const isOrangePin = (node.kind === "animate" && pin.pin === "output") || pin.pin === "motionInput";
                const isDependencyPin = pin.pin === "dependencyInput";
                const isConnHovered = connectionHoverPin?.nodeId === node.id && connectionHoverPin?.pin === pin.pin && connectionHoverPin?.index === pin.index;
                const radius = isConnHovered ? PIN_RADIUS + 3 : PIN_RADIUS;
                return (
                  <button
                    key={`${pin.pin}:${pin.index ?? 0}`}
                    type="button"
                    data-animation-pin={`${node.id}:${pin.pin}:${pin.index ?? 0}`}
                    className={`absolute rounded-full border border-[#101318] shadow-sm shadow-black/40 transition-all duration-75 ${isSequencePin ? "bg-emerald-500" : isDependencyPin ? "bg-pink-400" : isOrangePin ? "bg-purple-500" : "bg-sky-500"}`}
                    style={{
                      left: point.x - radius,
                      ...(isBottom ? { bottom: -radius } : { top: point.y - radius }),
                      width: radius * 2,
                      height: radius * 2,
                    }}
                    title={pinTitle(node, pin.pin, pin.index)}
                    aria-label={pinTitle(node, pin.pin, pin.index)}
                    onPointerDown={event => handlePinPointerDown(node, pin.pin, event, pin.index)}
                    onContextMenu={event => handlePinContextMenu(node, pin.pin, event, pin.index)}
                  />
                );
              })}
            </div>
          );
        })}
        {[connectionDragGhost, objectDragGhost].flatMap((ghost, i) => {
          if (!ghost) return [];
          const label = animationNodeKindLabel(ghost.kind);
          return [(
            <div
              key={i}
              className="pointer-events-none absolute origin-top-left rounded border border-dashed border-slate-500/50 bg-[#101318]/70 text-slate-400"
              style={{ width: NODE_WIDTH, transform: `translate(${ghost.point.x}px, ${ghost.point.y}px)` }}
            >
              <div className="flex h-9 items-center gap-2 px-3">
                <div className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-slate-500">{label}</span>
              </div>
            </div>
          )];
        })}
      </div>
      {objectPicker && onObjectConnect ? createPortal(
        <AnimationObjectPicker
          graph={graph}
          document={document}
          endpoint={objectPicker.endpoint}
          position={objectPicker.position}
          onConnect={onObjectConnect}
          onClose={() => setObjectPicker(undefined)}
        />,
        globalThis.document.body,
      ) : null}
    </div>
  );
});

export const AnimationGraphOverlay = memo(AnimationGraphOverlayImpl, (prev, next) =>
  prev.active === next.active &&
  prev.graph === next.graph &&
  prev.document === next.document &&
  prev.selectedNodeIds === next.selectedNodeIds &&
  prev.selectedConnectionIds === next.selectedConnectionIds &&
  prev.selectedObjectConnectionKeys === next.selectedObjectConnectionKeys &&
  prev.frames === next.frames &&
  prev.playing === next.playing &&
  prev.objectDragGhost === next.objectDragGhost &&
  prev.visibleViewportInsets === next.visibleViewportInsets &&
  prev.onAutopanScreenDelta === next.onAutopanScreenDelta &&
  prev.onObjectConnect === next.onObjectConnect
);

function AnimationObjectPicker({
  graph,
  document,
  endpoint,
  position,
  onConnect,
  onClose,
}: {
  graph: AnimationGraph;
  document: CurvaDocument;
  endpoint: AnimationEndpoint;
  position: { x: number; y: number };
  onConnect: (sourceNodeId: string, endpoint: AnimationEndpoint) => void;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [query, setQuery] = useState("");
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | undefined>(undefined);
  const [optimisticSelectedIds, setOptimisticSelectedIds] = useState<Set<string>>(() => objectIdsForEndpoint(graph, endpoint));
  const targetNode = graph.nodes.find(node => node.id === endpoint.nodeId);
  const multiple = targetNode?.kind === "group" && endpoint.pin === "input";
  const title = multiple ? "Connect Objects" : endpoint.pin === "dependencyInput" ? "Connect Dependency" : "Connect Object";

  useEffect(() => {
    searchRef.current?.focus();
    searchRef.current?.select();
  }, []);

  useEffect(() => {
    setOptimisticSelectedIds(objectIdsForEndpoint(graph, endpoint));
  }, [endpoint, graph]);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return getAllSceneNodes(document)
      .filter(isSelectableAnimationObject)
      .filter(node => {
        if (!needle) {
          return true;
        }
        return node.name.toLowerCase().includes(needle) || node.type.toLowerCase().includes(needle);
      });
  }, [document, query]);

  useEffect(() => {
    setHighlightedNodeId(rows[0]?.id);
  }, [rows]);

  useEffect(() => {
    function handlePointerDown(event: globalThis.PointerEvent): void {
      const target = event.target;
      if (!(target instanceof Node) || panelRef.current?.contains(target)) {
        return;
      }
      onClose();
    }

    function handleKeyDown(event: globalThis.KeyboardEvent): void {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        moveHighlight(1);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        moveHighlight(-1);
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        const highlighted = rows.find(node => node.id === highlightedNodeId) ?? rows[0];
        if (highlighted) {
          commitNode(highlighted);
        }
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    }

    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [highlightedNodeId, multiple, onClose, rows]);

  const style = floatingPickerStyle(position, 288, 384);

  return (
    <div
      ref={panelRef}
      className="fixed z-[70] w-72 rounded-md border border-white/10 bg-[#242a31] p-3 text-[12px] text-slate-300 shadow-2xl shadow-black/40"
      style={style}
      onPointerDown={event => event.stopPropagation()}
      onContextMenu={event => event.preventDefault()}
      onWheel={event => event.stopPropagation()}
      {...{ [panelFloatingUiAttribute]: "" }}
    >
      <div className="mb-3 flex min-w-0 items-center justify-between gap-2">
        <div className="min-w-0 truncate font-medium text-slate-100">{title}</div>
        <button
          type="button"
          className="shrink-0 rounded px-2 py-1 text-slate-400 hover:bg-white/10"
          onClick={onClose}
        >
          Close
        </button>
      </div>
      <div className="relative mb-2">
        <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-slate-500" aria-hidden="true" />
        <input
          ref={searchRef}
          type="search"
          value={query}
          onChange={event => setQuery(event.target.value)}
          onKeyDown={event => {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault();
              event.stopPropagation();
              moveHighlight(event.key === "ArrowDown" ? 1 : -1);
            }
          }}
          placeholder="Search objects"
          className="h-8 w-full rounded border border-white/10 bg-white/[0.04] pl-7 pr-2 text-[12px] text-slate-100 outline-none placeholder:text-slate-500 focus:border-sky-500/40"
        />
      </div>
      <div ref={listRef} className="max-h-72 overflow-y-auto rounded border border-white/10">
        {rows.length === 0 ? (
          <div className="px-2 py-3 text-center text-[11px] text-slate-500">No matching objects.</div>
        ) : rows.map(node => {
          const selected = optimisticSelectedIds.has(node.id);
          const highlighted = highlightedNodeId === node.id;
          return (
            <button
              key={node.id}
              ref={element => {
                if (element) {
                  rowRefs.current.set(node.id, element);
                } else {
                  rowRefs.current.delete(node.id);
                }
              }}
              type="button"
              className={`flex h-9 w-full items-center gap-2 px-2 text-left text-[12px] hover:bg-white/[0.06] ${highlighted ? "bg-sky-500/20 text-sky-100" : selected ? "bg-sky-500/15 text-sky-100" : "text-slate-200"}`}
              onMouseEnter={() => setHighlightedNodeId(node.id)}
              onClick={() => commitNode(node)}
              title={node.name}
            >
              <span className="grid size-4 shrink-0 place-items-center text-sky-300">
                {selected ? <Check className="size-3.5" aria-hidden="true" /> : null}
              </span>
              <span className="min-w-0 flex-1 truncate">{node.name}</span>
              <span className="shrink-0 text-[10px] uppercase text-slate-500">{nodeTypeLabel(node)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  function commitNode(node: SceneNode): void {
    if (!optimisticSelectedIds.has(node.id)) {
      onConnect(node.id, endpoint);
      setOptimisticSelectedIds(previous => new Set(previous).add(node.id));
    }
    if (!multiple) {
      onClose();
    }
  }

  function moveHighlight(delta: number): void {
    if (rows.length === 0) {
      return;
    }
    const currentIndex = Math.max(0, rows.findIndex(node => node.id === highlightedNodeId));
    const nextIndex = Math.min(rows.length - 1, Math.max(0, currentIndex + delta));
    const nextNode = rows[nextIndex];
    if (!nextNode) {
      return;
    }
    setHighlightedNodeId(nextNode.id);
    requestAnimationFrame(() => scrollObjectRowIntoView(nextNode.id));
  }

  function scrollObjectRowIntoView(nodeId: string): void {
    const row = rowRefs.current.get(nodeId);
    const list = listRef.current;
    if (!row || !list) {
      return;
    }
    const rowTop = row.offsetTop;
    const rowBottom = rowTop + row.offsetHeight;
    if (rowTop < list.scrollTop) {
      list.scrollTop = rowTop;
    } else if (rowBottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = rowBottom - list.clientHeight;
    }
  }
}

function AnimationNodePicker({
  graph,
  frames,
  selectedNodeId,
  position,
  onSelect,
  onClose,
}: {
  graph: AnimationGraph;
  frames: FrameNode[];
  selectedNodeId?: string;
  position: { x: number; y: number };
  onSelect: (nodeId: string) => void;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [query, setQuery] = useState("");
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | undefined>(undefined);

  useEffect(() => {
    searchRef.current?.focus();
    searchRef.current?.select();
  }, []);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return graph.nodes
      .filter((node): node is Extract<AnimationGraphNode, { kind: "animation" }> => node.kind === "animation")
      .filter(node => {
        if (!needle) {
          return true;
        }
        const frame = frames.find(candidate => candidate.id === node.frameId);
        return node.name.toLowerCase().includes(needle) || (frame?.name ?? "").toLowerCase().includes(needle);
      });
  }, [frames, graph.nodes, query]);

  useEffect(() => {
    setHighlightedNodeId(rows[0]?.id);
  }, [rows]);

  useEffect(() => {
    function handlePointerDown(event: globalThis.PointerEvent): void {
      const target = event.target;
      if (!(target instanceof Node) || panelRef.current?.contains(target)) {
        return;
      }
      onClose();
    }

    function handleKeyDown(event: globalThis.KeyboardEvent): void {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        moveHighlight(1);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        moveHighlight(-1);
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        const highlighted = rows.find(node => node.id === highlightedNodeId) ?? rows[0];
        if (highlighted) {
          onSelect(highlighted.id);
        }
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    }

    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [highlightedNodeId, onClose, onSelect, rows]);

  const style = floatingPickerStyle(position, 288, 384);

  return (
    <div
      ref={panelRef}
      className="fixed z-[70] w-72 rounded-md border border-white/10 bg-[#242a31] p-3 text-[12px] text-slate-300 shadow-2xl shadow-black/40"
      style={style}
      onContextMenu={event => event.preventDefault()}
      onWheel={event => event.stopPropagation()}
      {...{ [panelFloatingUiAttribute]: "" }}
    >
      <div className="mb-3 flex min-w-0 items-center justify-between gap-2">
        <div className="min-w-0 truncate font-medium text-slate-100">Target Animation</div>
        <button type="button" className="shrink-0 rounded px-2 py-1 text-slate-400 hover:bg-white/10" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="relative mb-2">
        <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-slate-500" aria-hidden="true" />
        <input
          ref={searchRef}
          type="search"
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder="Search animations"
          className="h-8 w-full rounded border border-white/10 bg-white/[0.04] pl-7 pr-2 text-[12px] text-slate-100 outline-none placeholder:text-slate-500 focus:border-orange-400/40"
        />
      </div>
      <div ref={listRef} className="max-h-72 overflow-y-auto rounded border border-white/10">
        {rows.length === 0 ? (
          <div className="px-2 py-3 text-center text-[11px] text-slate-500">No matching animations.</div>
        ) : rows.map(node => {
          const selected = selectedNodeId === node.id;
          const highlighted = highlightedNodeId === node.id;
          const frame = frames.find(candidate => candidate.id === node.frameId);
          return (
            <button
              key={node.id}
              ref={element => {
                if (element) {
                  rowRefs.current.set(node.id, element);
                } else {
                  rowRefs.current.delete(node.id);
                }
              }}
              type="button"
              className={`flex h-9 w-full items-center gap-2 px-2 text-left text-[12px] hover:bg-white/[0.06] ${highlighted ? "bg-orange-500/20 text-orange-100" : selected ? "bg-orange-500/15 text-orange-100" : "text-slate-200"}`}
              onMouseEnter={() => setHighlightedNodeId(node.id)}
              onClick={() => onSelect(node.id)}
              title={node.name}
            >
              <span className="grid size-4 shrink-0 place-items-center text-orange-300">
                {selected ? <Check className="size-3.5" aria-hidden="true" /> : null}
              </span>
              <span className="min-w-0 flex-1 truncate">{node.name}</span>
              <span className="shrink-0 text-[10px] text-slate-500">{frame?.name ?? "No frame"}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  function moveHighlight(delta: number): void {
    if (rows.length === 0) {
      return;
    }
    const currentIndex = Math.max(0, rows.findIndex(node => node.id === highlightedNodeId));
    const nextIndex = Math.min(rows.length - 1, Math.max(0, currentIndex + delta));
    const nextNode = rows[nextIndex];
    if (!nextNode) {
      return;
    }
    setHighlightedNodeId(nextNode.id);
    requestAnimationFrame(() => scrollAnimationRowIntoView(nextNode.id));
  }

  function scrollAnimationRowIntoView(nodeId: string): void {
    const row = rowRefs.current.get(nodeId);
    const list = listRef.current;
    if (!row || !list) {
      return;
    }
    const rowTop = row.offsetTop;
    const rowBottom = rowTop + row.offsetHeight;
    if (rowTop < list.scrollTop) {
      list.scrollTop = rowTop;
    } else if (rowBottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = rowBottom - list.clientHeight;
    }
  }
}

function VisualGroupFrame({
  group,
  bounds,
  renaming,
  renameValue,
  onRenameValueChange,
  onPointerDown,
  onTitlePointerDown,
  onRenameStart,
  onRenameCommit,
  onRenameKeyDown,
  onDelete,
  onToggleCollapsed,
  onWheel,
}: {
  group: AnimationGraphVisualGroup;
  bounds: { x: number; y: number; width: number; height: number };
  renaming: boolean;
  renameValue: string;
  onRenameValueChange: (value: string) => void;
  onPointerDown: (event: PointerEvent<HTMLDivElement>) => void;
  onTitlePointerDown: (event: PointerEvent<HTMLDivElement>) => void;
  onRenameStart: () => void;
  onRenameCommit: () => void;
  onRenameKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onDelete: () => void;
  onToggleCollapsed: () => void;
  onWheel: (event: WheelEvent<Element>) => void;
}) {
  return (
    <>
      {!group.collapsed ? (
        <div
          className="pointer-events-auto absolute origin-top-left rounded border border-slate-500/35 bg-slate-500/[0.055]"
          style={{ transform: `translate(${bounds.x}px, ${bounds.y}px)`, width: bounds.width, height: bounds.height }}
          onWheel={onWheel}
          onPointerDown={onPointerDown}
        />
      ) : null}
      <div
        className="pointer-events-auto absolute left-0 top-0 z-20 flex h-5 w-max min-w-16 max-w-56 select-none origin-top-left cursor-move items-center gap-1 rounded-sm border border-white/10 bg-[#20252b]/95 px-1 text-[11px] text-slate-300 shadow-sm shadow-black/30"
        style={{
          transform: `translate(${bounds.x}px, calc(${bounds.y}px - var(--animation-graph-label-offset, ${VISUAL_GROUP_LABEL_OFFSET_PX}px))) scale(var(--animation-graph-inverse-zoom, 1))`,
        }}
        onWheel={onWheel}
        onPointerDown={onTitlePointerDown}
      >
        {renaming ? (
          <input
            autoFocus
            value={renameValue}
            onChange={event => onRenameValueChange(event.currentTarget.value)}
            onBlur={onRenameCommit}
            onKeyDown={onRenameKeyDown}
            onPointerDown={event => event.stopPropagation()}
            onDoubleClick={event => event.stopPropagation()}
            onFocus={event => event.currentTarget.select()}
            className="h-4 min-w-20 flex-1 rounded-sm bg-white/[0.06] px-1 text-[11px] text-slate-100 outline-none"
          />
        ) : (
          <span
            className="min-w-0 max-w-40 select-none truncate px-0.5 font-medium"
            onDoubleClick={event => {
              event.preventDefault();
              event.stopPropagation();
              onRenameStart();
            }}
          >
            {group.name}
          </span>
        )}
        <button
          type="button"
          className="grid size-4 shrink-0 place-items-center rounded-sm text-slate-400 hover:bg-white/10 hover:text-slate-100"
          aria-label={group.collapsed ? "Expand animation group" : "Collapse animation group"}
          title={group.collapsed ? "Expand" : "Collapse"}
          onPointerDown={event => event.stopPropagation()}
          onClick={event => {
            event.stopPropagation();
            onToggleCollapsed();
          }}
        >
          {group.collapsed ? <ChevronRight className="size-3" aria-hidden="true" /> : <ChevronDown className="size-3" aria-hidden="true" />}
        </button>
        {!group.collapsed ? (
          <button
            type="button"
            className="grid size-4 shrink-0 place-items-center rounded-sm text-slate-400 hover:bg-white/10 hover:text-slate-100"
            aria-label="Ungroup animation nodes"
            title="Ungroup"
            onPointerDown={event => event.stopPropagation()}
            onClick={event => {
              event.stopPropagation();
              onDelete();
            }}
          >
            <Ungroup className="size-3" aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </>
  );
}

function NodeContent({
  node,
  graph,
  document,
  frames,
  playing,
  onUpdateNode,
  onAddAnimateProperty,
  onPlay,
  onPause,
  onSelectFlow,
  onFocus,
}: {
  node: AnimationGraphNode;
  graph: AnimationGraph;
  document: CurvaDocument;
  frames: FrameNode[];
  playing: boolean;
  onUpdateNode: (node: AnimationGraphNode, label?: string) => void;
  onAddAnimateProperty: () => void;
  onPlay: () => void;
  onPause: () => void;
  onSelectFlow: () => void;
  onFocus: () => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [renameName, setRenameName] = useState("");
  const [animationPicker, setAnimationPicker] = useState<AnimationPickerState | undefined>(undefined);
  const [expandedGroupBehaviorPropertyIds, setExpandedGroupBehaviorPropertyIds] = useState<Set<string>>(() => new Set());
  const titlePressRef = useRef<TitlePressState | undefined>(undefined);

  function startRename() {
    setRenameName(node.name);
    setRenaming(true);
  }

  function commitRename() {
    const next = renameName.trim();
    setRenaming(false);
    setRenameName("");
    if (next && next !== node.name) {
      onUpdateNode({ ...node, name: next } as AnimationGraphNode, "Renamed node");
    }
  }

  function cancelRename() {
    setRenaming(false);
    setRenameName("");
  }

  const controlProps = {
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      onFocus();
      event.stopPropagation();
    },
    onFocus,
  };
  const motionCanStutterTargets = node.kind === "motion" ? motionCanStutter(graph, node) : false;
  const motionHasGroupTargets = node.kind === "motion" ? motionHasGroupInput(graph, node) : false;
  const motionAnimatesElementsIndependently = node.kind === "motion" && node.animateElementsIndependently === true;
  const groupBehaviorOverridesActive = node.kind === "animate" ? animateNodeGroupOverridesActive(graph, node) : false;

  const targetStyle = node.kind === "animate" && node.target?.kind === "sceneNode"
    ? findSceneNode(document, node.target.nodeId)?.style
    : undefined;

  return (
    <div className="text-[12px]">
      <div className={`flex h-9 items-center gap-2 rounded-t border-b px-3 ${nodeHeaderClass(node)}`}>
        {renaming ? (
          <input
            autoFocus
            value={renameName}
            onChange={event => setRenameName(event.currentTarget.value)}
            onBlur={commitRename}
            onKeyDown={event => {
              if (event.key === "Enter") { event.preventDefault(); commitRename(); }
              else if (event.key === "Escape") { event.preventDefault(); cancelRename(); }
            }}
            onPointerDown={event => event.stopPropagation()}
            onDoubleClick={event => event.stopPropagation()}
            onFocus={event => event.currentTarget.select()}
            className="h-6 min-w-0 flex-1 rounded bg-white/[0.06] px-1 text-[12px] text-slate-100 outline-none"
          />
        ) : (
          <span
            className="min-w-0 flex-1 select-none truncate text-[12px] font-medium text-slate-200"
            title={node.name}
            onPointerDown={event => {
              if (isTitleDoublePress(titlePressRef, node.id, event)) {
                event.preventDefault();
                event.stopPropagation();
                startRename();
              }
            }}
            onDoubleClick={event => {
              event.preventDefault();
              event.stopPropagation();
              startRename();
            }}
          >
            {node.name}
          </span>
        )}
        <div className="flex shrink-0 items-center gap-1">
          {node.kind === "animation" ? (
            <>
              <button
                type="button"
                className="grid size-6 place-items-center rounded border border-white/10 bg-white/[0.04] text-slate-300 hover:bg-white/[0.08] hover:text-slate-100"
                aria-label="Select animation flow"
                title="Select animation flow"
                onPointerDown={event => {
                  onFocus();
                  event.stopPropagation();
                }}
                onClick={event => {
                  event.stopPropagation();
                  onSelectFlow();
                }}
              >
                <Workflow className="size-3" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="grid size-6 place-items-center rounded border border-white/10 bg-white/[0.04] hover:bg-white/[0.08]"
                aria-label={playing ? "Pause animation" : "Play animation"}
                title={playing ? "Pause" : "Play"}
                onPointerDown={event => {
                  onFocus();
                  event.stopPropagation();
                }}
                onClick={playing ? onPause : onPlay}
              >
                {playing ? <Pause className="size-3" aria-hidden="true" /> : <Play className="size-3" aria-hidden="true" />}
              </button>
            </>
          ) : null}
          {node.kind !== "animation" ? (
            <button
              type="button"
              className="grid size-6 place-items-center rounded border border-white/10 bg-white/[0.04] text-slate-400 hover:bg-white/[0.08] hover:text-slate-100"
              aria-label={node.collapsed ? "Expand node" : "Collapse node"}
              title={node.collapsed ? "Expand" : "Collapse"}
              onPointerDown={event => {
                onFocus();
                event.stopPropagation();
              }}
              onClick={event => {
                event.stopPropagation();
                onUpdateNode({ ...node, collapsed: !node.collapsed } as AnimationGraphNode, node.collapsed ? "Expanded node" : "Collapsed node");
              }}
            >
              {node.collapsed ? <ChevronRight className="size-3" aria-hidden="true" /> : <ChevronDown className="size-3" aria-hidden="true" />}
            </button>
          ) : null}
          <button
            type="button"
            className="grid size-6 place-items-center rounded border border-white/10 bg-white/[0.04] text-slate-400 hover:bg-white/[0.08] hover:text-slate-100"
            aria-label="Rename node"
            title="Rename"
            onPointerDown={event => {
              onFocus();
              event.stopPropagation();
            }}
            onClick={event => {
              event.stopPropagation();
              startRename();
            }}
          >
            <Pencil className="size-3" aria-hidden="true" />
          </button>
        </div>
      </div>
      {!isAnimationNodeCollapsed(node) ? (
      <div className="space-y-2 px-4 py-3">
        {node.kind === "animation" ? (
          <NodeSelect
            value={node.frameId ?? NO_FRAME_VALUE}
            options={[
              { value: NO_FRAME_VALUE, label: "No frame" },
              ...frames.map(frame => ({ value: frame.id, label: frame.name })),
            ]}
            onChange={value => onUpdateNode({ ...node, frameId: value === NO_FRAME_VALUE ? undefined : value })}
            onFocus={onFocus}
          />
        ) : null}
        {node.kind === "group" ? (
          <GroupNodeEditor
            node={node}
            document={document}
            onUpdateNode={onUpdateNode}
            onFocus={onFocus}
          />
        ) : null}
        {node.kind === "animate" ? (
          <div className="space-y-0 text-slate-300">
            {node.properties.map(property => {
              const expandedGroupBehavior = expandedGroupBehaviorPropertyIds.has(property.id);
              const overrideActiveFields = groupBehaviorOverridesActive ? activeGroupOverrideFieldKeys(property) : new Set<string>();
              return (
              <div key={property.id} className="space-y-2 border-t border-white/10 pt-2 pb-1">
                <div className="flex items-center gap-1">
                  <SearchableFloatingSelect
                    value={propertyOptionKind(property)}
                    options={ANIMATABLE_PROPERTY_OPTIONS.map(option => ({ value: option.kind, label: option.label }))}
                    onChange={value => onUpdateNode({
                      ...node,
                      properties: node.properties.map(candidate => candidate.id === property.id ? propertyForOption(value as AnimatablePropertyKind, property.id) : candidate),
                    }, "Changed animation property")}
                    onFocus={onFocus}
                    searchPlaceholder="Search properties"
                    triggerClassName={nodeSelectTriggerClass}
                    className="min-w-0 flex-1"
                  />
                  <button
                    type="button"
                    className="grid size-6 shrink-0 place-items-center rounded text-slate-400 hover:bg-red-500/15 hover:text-red-300"
                    title="Delete property"
                    aria-label="Delete property"
                    onPointerDown={event => {
                      onFocus();
                      event.stopPropagation();
                    }}
                    onClick={() => onUpdateNode({ ...node, properties: node.properties.filter(candidate => candidate.id !== property.id) }, "Deleted animation property")}
                  >
                    <Trash2 className="size-3" aria-hidden="true" />
                  </button>
                </div>
                <PropertyEditor
                  property={property}
                  targetStyle={targetStyle}
                  controlProps={controlProps}
                  onChange={nextProperty => onUpdateNode({
                    ...node,
                    properties: node.properties.map(candidate => candidate.id === property.id ? nextProperty : candidate),
                  }, "Changed animation property")}
                  overrideActiveFields={overrideActiveFields}
                />
                <GroupBehaviorEditor
                  property={property}
                  expanded={expandedGroupBehavior}
                  controlProps={controlProps}
                  onExpandedChange={expanded => {
                    setExpandedGroupBehaviorPropertyIds(current => {
                      const next = new Set(current);
                      if (expanded) {
                        next.add(property.id);
                      } else {
                        next.delete(property.id);
                      }
                      return next;
                    });
                  }}
                  onChange={nextProperty => onUpdateNode({
                    ...node,
                    properties: node.properties.map(candidate => candidate.id === property.id ? nextProperty : candidate),
                  }, "Changed group behavior")}
                />
              </div>
              );
            })}
            <div className="border-t border-white/10 pt-2">
              <button
                type="button"
                className="flex h-7 w-full items-center gap-1.5 rounded bg-white/[0.04] px-2 text-slate-400 hover:bg-white/[0.08] hover:text-slate-200"
                onPointerDown={event => {
                  onFocus();
                  event.stopPropagation();
                }}
                onClick={onAddAnimateProperty}
              >
                <Plus className="size-3.5" aria-hidden="true" />
                <span className="text-[11px]">Property</span>
              </button>
            </div>
          </div>
        ) : null}
        {node.kind === "motion" ? (
          <div className="space-y-2 pt-1 text-slate-300">
            <div className="grid grid-cols-2 gap-2">
              <NodeMetric label="Dur" value={node.durationMs} min={0} onCommit={value => onUpdateNode({ ...node, durationMs: value })} onFocus={onFocus} />
              <NodeMetric label="Off" value={node.offsetMs} onCommit={value => onUpdateNode({ ...node, offsetMs: value })} onFocus={onFocus} />
            </div>
            {motionHasGroupTargets ? (
              <NodeToggle
                label="Independent elements"
                checked={motionAnimatesElementsIndependently}
                onChange={checked => onUpdateNode({
                  ...node,
                  animateElementsIndependently: checked,
                  stutterMs: checked ? 0 : node.stutterMs,
                }, "Changed motion element animation")}
                onFocus={onFocus}
              />
            ) : null}
            {motionCanStutterTargets ? (
              <NodeMetric
                label="Stutter"
                value={motionAnimatesElementsIndependently ? 0 : node.stutterMs}
                min={0}
                disabled={motionAnimatesElementsIndependently}
                onCommit={value => onUpdateNode({ ...node, stutterMs: value }, "Changed motion stutter")}
                onFocus={onFocus}
              />
            ) : null}
            <button
              type="button"
              className={`flex h-7 w-full items-center justify-between rounded border px-2 text-[11px] ${
                node.transition
                  ? "border-orange-300/40 bg-orange-400/15 text-orange-100"
                  : "border-white/10 bg-white/[0.04] text-slate-400 hover:bg-white/[0.08] hover:text-slate-200"
              }`}
              onPointerDown={event => {
                onFocus();
                event.stopPropagation();
              }}
              onClick={event => {
                event.stopPropagation();
                onUpdateNode({ ...node, transition: !node.transition }, "Changed motion transition");
              }}
            >
              <span>Transition</span>
              <span className={`h-3.5 w-6 rounded-full border p-0.5 ${node.transition ? "border-orange-200/50 bg-orange-300/30" : "border-white/10 bg-black/20"}`}>
                <span className={`block size-2 rounded-full ${node.transition ? "translate-x-2.5 bg-orange-100" : "bg-slate-500"}`} />
              </span>
            </button>
            {node.transition ? (
              <div className="space-y-2">
                <button
                  type="button"
                  className="flex h-8 w-full items-center justify-between gap-2 rounded border border-white/10 bg-white/[0.04] px-2 text-left text-[11px] text-slate-300 hover:bg-white/[0.08]"
                  onPointerDown={event => {
                    onFocus();
                    event.stopPropagation();
                  }}
                  onClick={event => {
                    event.stopPropagation();
                    const rect = event.currentTarget.getBoundingClientRect();
                    setAnimationPicker({ position: { x: rect.left, y: rect.bottom + 6 } });
                  }}
                >
                  <span className="shrink-0 text-slate-500">Target</span>
                  <span className="min-w-0 flex-1 truncate text-right text-slate-200">
                    {animationTargetLabel(graph, frames, node.targetAnimationNodeId)}
                  </span>
                </button>
                <label className="space-y-1">
                  <span className="text-[11px] text-slate-500">Effect</span>
                  <NodeSelect
                    value={node.transitionEffect ?? "cut"}
                    options={ANIMATION_TRANSITION_EFFECTS.map(effect => ({ value: effect.name, label: effect.label }))}
                    onChange={(transitionEffect: AnimationTransitionEffectName) => {
                      onUpdateNode({ ...node, transitionEffect }, "Changed transition effect");
                    }}
                    onFocus={onFocus}
                  />
                </label>
                {(node.transitionEffect ?? "cut") === "fadeThroughColor" ? (
                  <label className="space-y-1">
                    <span className="text-[11px] text-slate-500">Color</span>
                    <ColorSwatchPicker
                      color={node.transitionColor ?? DEFAULT_TRANSITION_COLOR}
                      controlProps={{
                        onFocus,
                        onPointerDown: event => {
                          onFocus();
                          event.stopPropagation();
                        },
                      }}
                      onChange={transitionColor => onUpdateNode({ ...node, transitionColor }, "Changed transition color")}
                    />
                  </label>
                ) : null}
              </div>
            ) : null}
            <MotionEasingEditor node={node} onUpdateNode={onUpdateNode} onFocus={onFocus} />
            {animationPicker ? createPortal(
              <AnimationNodePicker
                graph={graph}
                frames={frames}
                selectedNodeId={node.targetAnimationNodeId}
                position={animationPicker.position}
                onSelect={targetAnimationNodeId => {
                  onUpdateNode({ ...node, targetAnimationNodeId }, "Changed transition target");
                  setAnimationPicker(undefined);
                }}
                onClose={() => setAnimationPicker(undefined)}
              />,
              globalThis.document.body,
            ) : null}
          </div>
        ) : null}
      </div>
      ) : null}
    </div>
  );
}

function GroupNodeEditor({
  node,
  document,
  onUpdateNode,
  onFocus,
}: {
  node: GroupAnimationNode;
  document: CurvaDocument;
  onUpdateNode: (node: AnimationGraphNode, label?: string) => void;
  onFocus: () => void;
}) {
  const singleTargetNode = node.targets.length === 1 && node.targets[0]?.kind === "sceneNode"
    ? findSceneNode(document, node.targets[0].nodeId)
    : undefined;
  const canRegroupText = singleTargetNode?.type === "text";
  const regroupFragments = canRegroupText && node.regroup !== "none"
    ? orderedRegroupFragments(splitTextNodeForRegroup(singleTargetNode, node.regroup), node.regroupOrder)
    : undefined;
  const listRows = regroupFragments?.map(fragment => ({
    id: fragment.id,
    label: regroupFragmentLabel(fragment.name),
    title: fragment.name,
    type: node.regroup === "words" ? "Word" : "Symbol",
    removable: false,
  })) ?? node.targets.map((target, index) => {
    const parentNode = "nodeId" in target ? findSceneNode(document, target.nodeId) : undefined;
    const parentName = parentNode?.name ?? "Missing object";
    switch (target.kind) {
      case "vectorVertex":
        return { id: `${animationTargetKey(target)}:${index}`, label: `${parentName} · vertex`, title: `${parentName} vertex`, type: "Vertex", removable: true };
      case "vectorHandle":
        return { id: `${animationTargetKey(target)}:${index}`, label: `${parentName} · handle`, title: `${parentName} handle`, type: "Handle", removable: true };
      case "vectorEdge":
        return { id: `${animationTargetKey(target)}:${index}`, label: `${parentName} · edge`, title: `${parentName} edge`, type: "Edge", removable: true };
      default:
        return {
          id: `${target.kind}:${target.kind === "sceneNode" ? target.nodeId : index}`,
          label: parentNode?.name ?? "Missing object",
          title: parentNode?.name ?? "Missing object",
          type: parentNode ? nodeTypeLabel(parentNode) : "Missing",
          removable: true,
        };
    }
  });

  function handleDrop(fromIndex: number, toIndex: number): void {
    if (regroupFragments) {
      const nextFragments = reorderArray(regroupFragments, fromIndex, toIndex);
      onUpdateNode({ ...node, regroupOrder: nextFragments.map(fragment => fragment.id) }, "Reordered regrouped text");
      return;
    }
    onUpdateNode({ ...node, targets: reorderArray(node.targets, fromIndex, toIndex) }, "Reordered animation group");
  }

  function invertRegroupOrder(): void {
    if (!regroupFragments) {
      return;
    }
    onUpdateNode({ ...node, regroupOrder: [...regroupFragments].reverse().map(fragment => fragment.id) }, "Inverted regrouped text order");
  }

  function randomizeRegroupOrder(): void {
    if (!regroupFragments) {
      return;
    }
    onUpdateNode({ ...node, regroupOrder: shuffledArray(regroupFragments).map(fragment => fragment.id) }, "Randomized regrouped text order");
  }

  function resetRegroupOrder(): void {
    if (!regroupFragments) {
      return;
    }
    onUpdateNode({ ...node, regroupOrder: undefined }, "Reset regrouped text order");
  }

  return (
    <div className="space-y-3 text-slate-300">
      {canRegroupText ? (
        <NodeLabeledSelect
          label="Regrouping"
          value={node.regroup}
          options={GROUP_REGROUP_OPTIONS}
          onChange={regroup => onUpdateNode({ ...node, regroup, regroupOrder: undefined }, "Changed group regrouping")}
          onFocus={onFocus}
        />
      ) : null}

      <div
        className={`mt-2 overflow-hidden rounded border border-white/10 bg-white/[0.025] ${regroupFragments ? "max-h-48 overflow-y-auto" : ""}`}
        onWheel={event => event.stopPropagation()}
      >
        {listRows.length === 0 ? (
          <div className="px-2 py-2 text-[11px] text-slate-500">No objects</div>
        ) : listRows.map((row, index) => {
          return (
            <div
              key={row.id}
              draggable
              className="flex h-8 items-center gap-1 border-b border-white/[0.06] px-1.5 last:border-b-0 hover:bg-white/[0.04]"
              onPointerDown={event => {
                onFocus();
                event.stopPropagation();
              }}
              onDragStart={event => handleGroupTargetDragStart(event, index)}
              onDragOver={event => {
                if (event.dataTransfer.types.includes(GROUP_TARGET_DRAG_MIME)) {
                  event.preventDefault();
                  event.stopPropagation();
                }
              }}
              onDrop={event => {
                event.preventDefault();
                event.stopPropagation();
                const fromIndex = Number.parseInt(event.dataTransfer.getData(GROUP_TARGET_DRAG_MIME), 10);
                if (!Number.isInteger(fromIndex) || fromIndex === index) {
                  return;
                }
                handleDrop(fromIndex, index);
              }}
            >
              <GripVertical className="size-3.5 shrink-0 text-slate-600" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate text-[11px] text-slate-200" title={row.title}>{row.label}</span>
              <span className="shrink-0 text-[9px] uppercase text-slate-600">{row.type}</span>
              {row.removable ? (
                <button
                  type="button"
                  className="grid size-5 shrink-0 place-items-center rounded-sm text-slate-500 hover:bg-red-500/15 hover:text-red-300"
                  title="Remove object"
                  aria-label="Remove object"
                  onPointerDown={event => {
                    onFocus();
                    event.stopPropagation();
                  }}
                  onClick={event => {
                    event.stopPropagation();
                    onUpdateNode({ ...node, targets: node.targets.filter((_, targetIndex) => targetIndex !== index) }, "Updated animation group");
                  }}
                >
                  <X className="size-3" aria-hidden="true" />
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      {regroupFragments && regroupFragments.length > 1 ? (
        <div className="mt-2 space-y-1.5">
          <div className="flex gap-1.5">
            <button
                type="button"
                className="flex h-7 flex-1 items-center justify-center gap-1.5 rounded border border-white/10 bg-white/[0.04] px-2 text-[11px] font-medium text-slate-300 hover:bg-white/[0.08] hover:text-slate-100"
                onPointerDown={event => {
                  onFocus();
                  event.stopPropagation();
                }}
                onClick={event => {
                  event.stopPropagation();
                  invertRegroupOrder();
                }}
            >
              <ArrowUpDown className="size-3.5" aria-hidden="true" />
              Inverse
            </button>
            <button
                type="button"
                className="flex h-7 flex-1 items-center justify-center gap-1.5 rounded border border-white/10 bg-white/[0.04] px-2 text-[11px] font-medium text-slate-300 hover:bg-white/[0.08] hover:text-slate-100"
                onPointerDown={event => {
                  onFocus();
                  event.stopPropagation();
                }}
                onClick={event => {
                  event.stopPropagation();
                  randomizeRegroupOrder();
                }}
            >
              <Shuffle className="size-3.5" aria-hidden="true" />
              Randomize
            </button>
          </div>
          <button
              type="button"
              className="flex h-7 w-full items-center justify-center gap-1.5 rounded border border-white/10 bg-white/[0.025] px-2 text-[11px] font-medium text-slate-400 hover:bg-white/[0.07] hover:text-slate-100"
              onPointerDown={event => {
                onFocus();
                event.stopPropagation();
              }}
              onClick={event => {
                event.stopPropagation();
                resetRegroupOrder();
              }}
          >
            <RotateCcw className="size-3.5" aria-hidden="true" />
            Reset order
          </button>
        </div>
      ) : null}
    </div>
  );
}

function shuffledArray<T extends { id: string }>(items: T[]): T[] {
  const shuffled = [...items];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex]!, shuffled[index]!];
  }
  if (shuffled.length > 1 && shuffled.every((item, index) => item.id === items[index]?.id)) {
    [shuffled[0], shuffled[1]] = [shuffled[1]!, shuffled[0]!];
  }
  return shuffled;
}

function orderedRegroupFragments<T extends { id: string }>(fragments: T[], order: string[] | undefined): T[] {
  if (!order?.length) {
    return fragments;
  }
  const byId = new Map(fragments.map(fragment => [fragment.id, fragment] as const));
  const ordered: T[] = [];
  const seen = new Set<string>();
  for (const id of order) {
    const fragment = byId.get(id);
    if (fragment && !seen.has(id)) {
      ordered.push(fragment);
      seen.add(id);
    }
  }
  for (const fragment of fragments) {
    if (!seen.has(fragment.id)) {
      ordered.push(fragment);
    }
  }
  return ordered;
}

function regroupFragmentLabel(value: string): string {
  if (value === " ") {
    return "Space";
  }
  if (value === "\t") {
    return "Tab";
  }
  if (value.trim().length === 0) {
    return "Whitespace";
  }
  return value;
}

function handleGroupTargetDragStart(event: DragEvent<HTMLDivElement>, index: number): void {
  event.stopPropagation();
  event.dataTransfer.effectAllowed = "move";
  event.dataTransfer.setData(GROUP_TARGET_DRAG_MIME, String(index));
}

function reorderArray<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  if (fromIndex < 0 || fromIndex >= items.length || toIndex < 0 || toIndex >= items.length) {
    return items;
  }
  const next = [...items];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item!);
  return next;
}

function nodeHeaderClass(node: AnimationGraphNode): string {
  switch (node.kind) {
    case "motion":
      return node.transition ? "border-orange-300/15 bg-orange-400/[0.10]" : "border-emerald-300/10 bg-emerald-400/[0.08]";
    case "animate":
      return "border-purple-300/10 bg-purple-400/[0.08]";
    default:
      return "border-white/10 bg-white/[0.025]";
  }
}

function animationNodeKindLabel(kind: AnimationNodeKind): string {
  switch (kind) {
    case "animation":
      return "Animation";
    case "group":
      return "Group";
    case "animate":
      return "Behavior";
    case "motion":
      return "Motion";
  }
}

function pinsForNode(node: AnimationGraphNode, graph?: AnimationGraph): Array<{ pin: AnimationPinKind; index?: number }> {
  switch (node.kind) {
    case "animation":
      return [{ pin: "output" }];
    case "group":
      return [{ pin: "input" }, { pin: "output" }];
    case "animate": {
      return [
        { pin: "input" },
        ...node.properties.flatMap((property, index) => propertyHasDependencyInput(property)
          ? [{ pin: "dependencyInput" as const, index }]
          : []),
        { pin: "output" },
      ];
    }
    case "motion":
      return [
        { pin: "motionSequenceInput" },
        ...motionInputIndexes(graph, node.id).map(index => ({ pin: "motionInput" as const, index })),
        { pin: "motionInput", index: nextMotionInputIndex(graph, node.id) },
        { pin: "motionSequenceOutput" },
      ];
  }
}

/** Pins still interactive while the node body is collapsed (header-only). */
function isPinVisibleOnNode(node: AnimationGraphNode, pin: AnimationPinKind): boolean {
  if (!isAnimationNodeCollapsed(node)) {
    return true;
  }
  // Collapsed groups keep the right-side output so you can wire them without expanding.
  return node.kind === "group" && pin === "output";
}

type ControlProps = { onPointerDown: (event: PointerEvent<HTMLElement>) => void; onFocus: () => void };

function ColorSwatchPicker({
  color,
  controlProps,
  onChange,
}: {
  color: OklchColor;
  controlProps: ControlProps;
  onChange: (color: OklchColor) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const paint: Paint = createSolidPaint(color);

  return (
    <>
      <button
        type="button"
        className="flex h-7 w-full items-center gap-2 rounded border border-white/10 bg-white/[0.04] px-2 hover:bg-white/[0.08]"
        onPointerDown={event => {
          controlProps.onPointerDown(event);
          event.stopPropagation();
        }}
        onClick={() => setPickerOpen(open => !open)}
      >
        <div
          className="h-4 w-4 shrink-0 rounded-sm border border-white/20"
          style={{ background: paintPreviewBackground(paint) }}
        />
        <span className="text-[11px] font-mono text-slate-300">{oklchToHex(color)}</span>
      </button>
      {pickerOpen ? createPortal(
        <ColorPickerPopover
          title="Target color"
          paint={paint}
          allowImage={false}
          onChange={nextPaint => {
            if (nextPaint.type === "solid") {
              onChange(nextPaint.color);
            }
          }}
          onColorSampleRequest={() => {}}
          onColorSampleModeChange={() => {}}
          onClose={() => setPickerOpen(false)}
        />,
        globalThis.document.body,
      ) : null}
    </>
  );
}

function ColorChannelEditor({
  colorSpace,
  color,
  controlProps,
  overrideActiveFields = new Set<string>(),
  onColorSpaceChange,
  onColorChange,
  onChannelChange,
}: {
  colorSpace: ColorAnimationSpace;
  color: OklchColor;
  controlProps: ControlProps;
  overrideActiveFields?: Set<string>;
  onColorSpaceChange: (space: ColorAnimationSpace) => void;
  onColorChange: (color: OklchColor) => void;
  onChannelChange: (channel: string, displayValue: number) => void;
}) {
  const channelFields = colorAnimationChannelFields(colorSpace, color);
  return (
    <div className="space-y-1.5">
      <div
        onPointerDownCapture={controlProps.onFocus}
        onPointerDown={event => event.stopPropagation()}
        onFocusCapture={controlProps.onFocus}
      >
        <PanelSelect
          value={colorSpace}
          onValueChange={value => onColorSpaceChange(value as ColorAnimationSpace)}
          triggerClassName={nodeSelectTriggerClass}
          contentClassName={nodeSelectContentClass}
          contentPosition={nodeSelectContentPosition}
          contentAvoidCollisions={false}
        >
          {COLOR_SPACE_OPTIONS.map(opt => (
            <PanelSelectItem key={opt.value} value={opt.value}>{opt.label}</PanelSelectItem>
          ))}
        </PanelSelect>
      </div>
      <ColorSwatchPicker color={color} controlProps={controlProps} onChange={onColorChange} />
      <div className="grid grid-cols-2 gap-2">
        {channelFields.map(field => (
          <NodeMetric
            key={field.key}
            label={field.label}
            value={field.value}
            min={field.min}
            max={field.max}
            step={field.step}
            overridden={overrideActiveFields.has(field.key)}
            onCommit={value => onChannelChange(field.key, value)}
            onFocus={controlProps.onFocus}
          />
        ))}
      </div>
    </div>
  );
}

function MotionEasingEditor({
  node,
  onUpdateNode,
  onFocus,
}: {
  node: MotionAnimationNode;
  onUpdateNode: (node: AnimationGraphNode, label?: string) => void;
  onFocus: () => void;
}) {
  if (node.durationMs <= 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <span className="text-[11px] text-slate-500">Easing</span>
        <NodeSelect
          value={motionEasingOptionValue(node)}
          options={MOTION_EASING_OPTIONS}
          onChange={value => {
            const easing = value === "wiggle"
              ? DEFAULT_WIGGLE_EASING
              : { kind: "preset" as const, name: value.replace("preset:", "") as AnimationEasingName };
            onUpdateNode({ ...node, easing }, "Changed motion easing");
          }}
          onFocus={onFocus}
        />
      </div>
      {node.easing.kind === "wiggle" ? (
        <WiggleEasingControls node={node} easing={node.easing} onUpdateNode={onUpdateNode} onFocus={onFocus} />
      ) : null}
      <EasingPreview easing={node.easing} onFocus={onFocus} />
    </div>
  );
}

function WiggleEasingControls({
  node,
  easing,
  onUpdateNode,
  onFocus,
}: {
  node: MotionAnimationNode;
  easing: Extract<MotionAnimationNode["easing"], { kind: "wiggle" }>;
  onUpdateNode: (node: AnimationGraphNode, label?: string) => void;
  onFocus: () => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <NodeMetric label="Freq" value={easing.frequency} min={0} step={0.25} onCommit={value => onUpdateNode({ ...node, easing: { ...easing, frequency: value } }, "Changed wiggle easing")} onFocus={onFocus} />
      <NodeMetric label="Phase" value={easing.phase} step={0.1} onCommit={value => onUpdateNode({ ...node, easing: { ...easing, phase: value } }, "Changed wiggle easing")} onFocus={onFocus} />
      <NodeMetric label="Amp" value={easing.amplitude} step={0.1} onCommit={value => onUpdateNode({ ...node, easing: { ...easing, amplitude: value } }, "Changed wiggle easing")} onFocus={onFocus} />
      <NodeMetric label="Decay" value={easing.decay} min={0} step={0.1} onCommit={value => onUpdateNode({ ...node, easing: { ...easing, decay: Math.max(0, value) } }, "Changed wiggle easing")} onFocus={onFocus} />
    </div>
  );
}

function EasingPreview({
  easing,
  onFocus,
}: {
  easing: MotionAnimationNode["easing"];
  onFocus: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [playing, setPlaying] = useState(false);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const previewCanvas = canvas;

    const ease = animationEasingFunction(easing);
    let raf = 0;
    let startTime: number | undefined;

    function draw(timestamp: number = 0) {
      if (startTime === undefined) startTime = timestamp;
      const rect = previewCanvas.getBoundingClientRect();
      const width = Math.max(1, rect.width);
      const height = Math.max(1, rect.height);
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      const pixelWidth = Math.round(width * dpr);
      const pixelHeight = Math.round(height * dpr);
      if (previewCanvas.width !== pixelWidth || previewCanvas.height !== pixelHeight) {
        previewCanvas.width = pixelWidth;
        previewCanvas.height = pixelHeight;
      }

      const ctx = previewCanvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);

      const samples = sampleEasing(ease);
      const minValue = Math.min(0, ...samples.map(sample => sample.y));
      const maxValue = Math.max(1, ...samples.map(sample => sample.y));
      const padding = Math.max(0.08, (maxValue - minValue) * 0.08);
      const minY = minValue - padding;
      const maxY = maxValue + padding;
      const xFor = (x: number) => 8 + x * (width - 26);
      const yFor = (y: number) => 7 + (1 - (y - minY) / Math.max(0.001, maxY - minY)) * (height - 14);

      ctx.lineWidth = 1;
      ctx.strokeStyle = "rgba(148, 163, 184, 0.18)";
      for (const value of [0, 1]) {
        const y = yFor(value);
        ctx.beginPath();
        ctx.moveTo(8, y);
        ctx.lineTo(width - 8, y);
        ctx.stroke();
      }

      ctx.strokeStyle = "rgba(52, 211, 153, 0.9)";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      for (const [index, sample] of samples.entries()) {
        const x = xFor(sample.x);
        const y = yFor(sample.y);
        if (index === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();

      const progress = playing ? ((timestamp - startTime) % 1200) / 1200 : 0;
      const value = ease(progress);
      const ballX = width - 10;
      const ballY = yFor(value);
      ctx.strokeStyle = "rgba(52, 211, 153, 0.35)";
      ctx.beginPath();
      ctx.moveTo(xFor(progress), 7);
      ctx.lineTo(xFor(progress), height - 7);
      ctx.stroke();
      ctx.fillStyle = "rgb(125, 211, 252)";
      ctx.beginPath();
      ctx.arc(ballX, ballY, 4, 0, Math.PI * 2);
      ctx.fill();

      if (playing) {
        raf = requestAnimationFrame(draw);
      }
    }

    if (playing) {
      raf = requestAnimationFrame(draw);
    } else {
      draw();
    }
    return () => cancelAnimationFrame(raf);
  }, [easing, playing]);

  return (
    <div
      className="rounded border border-white/10 bg-black/20 p-1.5"
      onPointerDownCapture={onFocus}
      onPointerDown={event => event.stopPropagation()}
      onFocusCapture={onFocus}
    >
      <div className="flex items-center gap-1">
        <canvas ref={canvasRef} className="h-[74px] min-w-0 flex-1 rounded-sm bg-white/[0.025]" />
        <button
          type="button"
          className="grid size-7 shrink-0 place-items-center rounded border border-white/10 bg-white/[0.04] text-slate-400 hover:bg-white/[0.08] hover:text-slate-100"
          aria-label={playing ? "Pause easing preview" : "Play easing preview"}
          title={playing ? "Pause" : "Play"}
          onPointerDown={event => {
            onFocus();
            event.stopPropagation();
          }}
          onClick={() => {
            setPlaying(value => !value);
          }}
        >
          {playing ? <Pause className="size-3.5" aria-hidden="true" /> : <Play className="size-3.5" aria-hidden="true" />}
        </button>
      </div>
    </div>
  );
}

function motionEasingOptionValue(node: MotionAnimationNode): MotionEasingOption {
  return node.easing.kind === "wiggle" ? "wiggle" : `preset:${node.easing.name}`;
}

function sampleEasing(ease: (x: number) => number): Array<{ x: number; y: number }> {
  const count = 72;
  const samples: Array<{ x: number; y: number }> = [];
  for (let i = 0; i <= count; i += 1) {
    const x = i / count;
    samples.push({ x, y: ease(x) });
  }
  return samples;
}

function fillIndexOptions(style: NodeStyle | undefined, kind: "fill" | "stroke"): Array<{ value: string; label: string }> {
  if (!style) {
    return [{ value: "0", label: kind === "fill" ? "Fill 1" : "Stroke 1" }];
  }
  const items = kind === "fill" ? style.fills : normalizeStrokeStack(style);
  if (items.length === 0) {
    return [{ value: "0", label: kind === "fill" ? "Fill 1" : "Stroke 1" }];
  }
  return items.map((_, i) => ({ value: String(i), label: `${kind === "fill" ? "Fill" : "Stroke"} ${i + 1}` }));
}

function effectIndexOptions(style: NodeStyle | undefined): Array<{ value: string; label: string }> {
  const effects = style?.effects ?? [];
  if (effects.length === 0) {
    return [{ value: "0", label: "Effect 1" }];
  }
  return effects.map((effect, i) => ({
    value: String(i),
    label: `${i + 1} – ${effect.type === "dropShadow" ? "Drop Shadow" : effect.type === "innerShadow" ? "Inner Shadow" : effect.type === "layerBlur" ? "Layer Blur" : "Bg Blur"}`,
  }));
}

function PropertyEditor({
  property,
  targetStyle,
  controlProps,
  onChange,
  overrideActiveFields = new Set<string>(),
}: {
  property: AnimationProperty;
  targetStyle?: NodeStyle;
  controlProps: ControlProps;
  onChange: (property: AnimationProperty) => void;
  overrideActiveFields?: Set<string>;
}) {
  if (property.kind === "pin") {
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <NodeLabeledSelect
            label="Pin"
            value={property.anchor}
            options={(isDistancePinTarget(property.target)
              ? ANIMATION_ANCHOR_OPTIONS.filter(option => option.anchor === "center")
              : ANIMATION_ANCHOR_OPTIONS
            ).map(option => ({ value: option.anchor, label: option.label }))}
            onChange={value => onChange({ ...property, anchor: value as AnimationAnchor })}
            onFocus={controlProps.onFocus}
          />
          <SearchableFloatingSelect
            label="Target"
            value={pinTargetOption(property)}
            options={PIN_TARGET_OPTIONS}
            onChange={value => onChange(pinWithTargetOption(property, value as PinTargetOption))}
            onFocus={controlProps.onFocus}
            searchPlaceholder="Search targets"
            triggerClassName={nodeSelectTriggerClass}
          />
        </div>
        {property.target?.kind === "pathPercent" ? (
          <div className="grid grid-cols-2 gap-2">
            <NodeMetric
              label="Path %"
              value={property.target.progress * 100}
              min={0}
              max={100}
              step={0.1}
              overridden={overrideActiveFields.has("target.progress")}
              onCommit={value => onChange({ ...property, target: { kind: "pathPercent", progress: Math.max(0, Math.min(100, value)) / 100 } })}
              onFocus={controlProps.onFocus}
            />
          </div>
        ) : isDistancePinTarget(property.target) ? (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <NodeMetric
                label={distancePinMetricLabel(property.target.kind)}
                value={property.target.distance}
                min={0}
                overridden={overrideActiveFields.has("target.distance")}
                onCommit={value => {
                  if (!isDistancePinTarget(property.target)) {
                    return;
                  }
                  onChange({
                    ...property,
                    target: { ...property.target, distance: Math.max(0, value) },
                  });
                }}
                onFocus={controlProps.onFocus}
              />
            </div>
            {isClampedDistancePinTarget(property.target) ? (
              <NodeToggle
                label="Snap back"
                checked={distancePinSnapBack(property.target)}
                onChange={snapBack => {
                  if (!isClampedDistancePinTarget(property.target)) {
                    return;
                  }
                  onChange({
                    ...property,
                    target: snapBack
                      ? { ...property.target, snapBack: true }
                      : { kind: property.target.kind, distance: property.target.distance },
                  });
                }}
                onFocus={controlProps.onFocus}
              />
            ) : null}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <NodeMetric label={pinAxisLabel(property, "x")} value={property.x} overridden={overrideActiveFields.has("x")} onCommit={value => onChange({ ...property, x: value })} onFocus={controlProps.onFocus} />
            <NodeMetric label={pinAxisLabel(property, "y")} value={property.y} overridden={overrideActiveFields.has("y")} onCommit={value => onChange({ ...property, y: value })} onFocus={controlProps.onFocus} />
          </div>
        )}
      </div>
    );
  }

  if (property.kind === "followPath") {
    return (
      <div className="space-y-2">
        <NodeToggle
          label="Orient to path"
          checked={Boolean(property.orientToPath)}
          onChange={orientToPath => onChange({ ...property, orientToPath })}
          onFocus={controlProps.onFocus}
        />
        <div className="grid grid-cols-2 gap-2">
          <NodeLabeledSelect
            label={""}
            value={property.anchor}
            options={ANIMATION_ANCHOR_OPTIONS.map(option => ({ value: option.anchor, label: option.label }))}
            onChange={value => onChange({ ...property, anchor: value as AnimationAnchor })}
            onFocus={controlProps.onFocus}
          />
          <NodeMetric
            label="To %"
            value={property.progress * 100}
            min={0}
            max={100}
            step={0.1}
            overridden={overrideActiveFields.has("progress")}
            onCommit={value => onChange({ ...property, progress: Math.max(0, Math.min(100, value)) / 100 })}
            onFocus={controlProps.onFocus}
          />
        </div>
      </div>
    );
  }

  if (property.kind === "strokePath") {
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <NodeToggle
            label="Inverse"
            checked={property.inverse}
            onChange={inverse => onChange({ ...property, inverse })}
            onFocus={controlProps.onFocus}
          />
          <NodeToggle
            label="Erase"
            checked={property.erase}
            onChange={erase => onChange({ ...property, erase })}
            onFocus={controlProps.onFocus}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <NodeMetric
            label="To %"
            value={property.progress * 100}
            min={0}
            max={100}
            step={0.1}
            overridden={overrideActiveFields.has("progress")}
            onCommit={value => onChange({ ...property, progress: Math.max(0, Math.min(100, value)) / 100 })}
            onFocus={controlProps.onFocus}
          />
        </div>
      </div>
    );
  }

  if (property.kind === "opacity") {
    return (
      <NodeMetric
        label="%"
        value={opacityToPercent(property.value)}
        min={0}
        max={100}
        overridden={overrideActiveFields.has("value")}
        onCommit={value => onChange({ ...property, value: Math.max(0, Math.min(100, value)) / 100 })}
        onFocus={controlProps.onFocus}
      />
    );
  }

  if (property.kind === "layout" || property.kind === "stack" || property.kind === "inline") {
    return (
      <NodeMetric label="Gap" value={property.gap} overridden={overrideActiveFields.has("gap")} onCommit={value => onChange({ ...property, gap: value })} onFocus={controlProps.onFocus} />
    );
  }

  if (property.kind === "fitToBBox") {
    return (
      <NodeMetric label="Pad" value={property.pad} overridden={overrideActiveFields.has("pad")} onCommit={value => onChange({ ...property, pad: value })} onFocus={controlProps.onFocus} />
    );
  }

  if (property.kind === "orbit") {
    return (
      <div className="grid grid-cols-2 gap-2">
        <NodeMetric label="Deg" value={property.angle} overridden={overrideActiveFields.has("angle")} onCommit={angle => onChange({ ...property, angle })} onFocus={controlProps.onFocus} />
        <NodeMetric label="Rev" value={property.angle / 360} step={0.25} overridden={overrideActiveFields.has("angle")} onCommit={revolutions => onChange({ ...property, angle: revolutions * 360 })} onFocus={controlProps.onFocus} />
      </div>
    );
  }

  if (property.kind === "lookAt") {
    return <div className="text-[11px] text-slate-500">Tracks the connected target</div>;
  }

  if (property.kind === "rotation") {
    return (
      <NodeMetric label="Deg" value={property.value} overridden={overrideActiveFields.has("value")} onCommit={value => onChange({ ...property, value })} onFocus={controlProps.onFocus} />
    );
  }

  if (property.kind === "fillColor") {
    const indexOptions = fillIndexOptions(targetStyle, "fill");
    return (
      <div className="space-y-1.5">
        {indexOptions.length > 1 && (
          <div
            onPointerDownCapture={controlProps.onFocus}
            onPointerDown={event => event.stopPropagation()}
            onFocusCapture={controlProps.onFocus}
          >
            <PanelSelect
              value={String(property.fillIndex)}
              onValueChange={value => onChange({ ...property, fillIndex: Number(value) })}
              triggerClassName={nodeSelectTriggerClass}
              contentClassName={nodeSelectContentClass}
              contentPosition={nodeSelectContentPosition}
              contentAvoidCollisions={false}
            >
              {indexOptions.map(opt => (
                <PanelSelectItem key={opt.value} value={opt.value}>{opt.label}</PanelSelectItem>
              ))}
            </PanelSelect>
          </div>
        )}
        <ColorChannelEditor
          colorSpace={property.colorSpace}
          color={property.color}
          controlProps={controlProps}
          overrideActiveFields={overrideActiveFields}
          onColorSpaceChange={colorSpace => onChange(animationPropertyWithColorSpace(property, colorSpace))}
          onColorChange={color => onChange({ ...property, color, channels: allColorAnimationChannels(property.colorSpace) })}
          onChannelChange={(channel, value) => onChange(animationPropertyWithColorChannelValue(property, channel, value))}
        />
      </div>
    );
  }

  if (property.kind === "strokeColor") {
    const indexOptions = fillIndexOptions(targetStyle, "stroke");
    return (
      <div className="space-y-1.5">
        {indexOptions.length > 1 && (
          <div
            onPointerDownCapture={controlProps.onFocus}
            onPointerDown={event => event.stopPropagation()}
            onFocusCapture={controlProps.onFocus}
          >
            <PanelSelect
              value={String(property.strokeIndex)}
              onValueChange={value => onChange({ ...property, strokeIndex: Number(value) })}
              triggerClassName={nodeSelectTriggerClass}
              contentClassName={nodeSelectContentClass}
              contentPosition={nodeSelectContentPosition}
              contentAvoidCollisions={false}
            >
              {indexOptions.map(opt => (
                <PanelSelectItem key={opt.value} value={opt.value}>{opt.label}</PanelSelectItem>
              ))}
            </PanelSelect>
          </div>
        )}
        <ColorChannelEditor
          colorSpace={property.colorSpace}
          color={property.color}
          controlProps={controlProps}
          overrideActiveFields={overrideActiveFields}
          onColorSpaceChange={colorSpace => onChange(animationPropertyWithColorSpace(property, colorSpace))}
          onColorChange={color => onChange({ ...property, color, channels: allColorAnimationChannels(property.colorSpace) })}
          onChannelChange={(channel, value) => onChange(animationPropertyWithColorChannelValue(property, channel, value))}
        />
      </div>
    );
  }

  if (property.kind === "effectValue") {
    const indexOptions = effectIndexOptions(targetStyle);
    const effectField: string = property.field;
    return (
      <div className="space-y-1.5">
        <div className={indexOptions.length > 1 ? "grid grid-cols-2 gap-1.5" : ""}>
          {indexOptions.length > 1 && (
            <div
              onPointerDownCapture={controlProps.onFocus}
              onPointerDown={event => event.stopPropagation()}
              onFocusCapture={controlProps.onFocus}
            >
              <PanelSelect
                value={String(property.effectIndex)}
                onValueChange={value => onChange({ ...property, effectIndex: Number(value) })}
                triggerClassName={nodeSelectTriggerClass}
                contentClassName={nodeSelectContentClass}
                contentPosition={nodeSelectContentPosition}
                contentAvoidCollisions={false}
              >
                {indexOptions.map(opt => (
                  <PanelSelectItem key={opt.value} value={opt.value}>{opt.label}</PanelSelectItem>
                ))}
              </PanelSelect>
            </div>
          )}
          <div
            onPointerDownCapture={controlProps.onFocus}
            onPointerDown={event => event.stopPropagation()}
            onFocusCapture={controlProps.onFocus}
          >
            <PanelSelect
              value={effectField}
              onValueChange={value => {
                if (value === "color") {
                  onChange({
                    id: property.id,
                    kind: "effectColor",
                    effectIndex: property.effectIndex,
                    colorSpace: "oklch",
                    channels: DEFAULT_ANIM_CHANNELS,
                    color: DEFAULT_ANIM_COLOR,
                  });
                } else {
                  onChange({ ...property, field: value as EffectAnimationField });
                }
              }}
              triggerClassName={nodeSelectTriggerClass}
              contentClassName={nodeSelectContentClass}
              contentPosition={nodeSelectContentPosition}
              contentAvoidCollisions={false}
            >
              {EFFECT_FIELD_OPTIONS.map(opt => (
                <PanelSelectItem key={opt.value} value={opt.value}>{opt.label}</PanelSelectItem>
              ))}
            </PanelSelect>
          </div>
        </div>
        <NodeMetric
          label={effectField === "opacity" ? "%" : "px"}
          value={property.value}
          overridden={overrideActiveFields.has("value")}
          onCommit={value => onChange({ ...property, value })}
          onFocus={controlProps.onFocus}
        />
      </div>
    );
  }

  if (property.kind === "effectColor") {
    const indexOptions = effectIndexOptions(targetStyle);
    return (
      <div className="space-y-1.5">
        <div className={indexOptions.length > 1 ? "grid grid-cols-2 gap-1.5" : ""}>
          {indexOptions.length > 1 && (
            <div
              onPointerDownCapture={controlProps.onFocus}
              onPointerDown={event => event.stopPropagation()}
              onFocusCapture={controlProps.onFocus}
            >
              <PanelSelect
                value={String(property.effectIndex)}
                onValueChange={value => onChange({ ...property, effectIndex: Number(value) })}
                triggerClassName={nodeSelectTriggerClass}
                contentClassName={nodeSelectContentClass}
                contentPosition={nodeSelectContentPosition}
                contentAvoidCollisions={false}
              >
                {indexOptions.map(opt => (
                  <PanelSelectItem key={opt.value} value={opt.value}>{opt.label}</PanelSelectItem>
                ))}
              </PanelSelect>
            </div>
          )}
          <div
            onPointerDownCapture={controlProps.onFocus}
            onPointerDown={event => event.stopPropagation()}
            onFocusCapture={controlProps.onFocus}
          >
            <PanelSelect
              value="color"
              onValueChange={value => {
                if (value !== "color") {
                  onChange({
                    id: property.id,
                    kind: "effectValue",
                    effectIndex: property.effectIndex,
                    field: value as EffectAnimationField,
                    value: 0,
                  });
                }
              }}
              triggerClassName={nodeSelectTriggerClass}
              contentClassName={nodeSelectContentClass}
              contentPosition={nodeSelectContentPosition}
              contentAvoidCollisions={false}
            >
              {EFFECT_FIELD_OPTIONS.map(opt => (
                <PanelSelectItem key={opt.value} value={opt.value}>{opt.label}</PanelSelectItem>
              ))}
            </PanelSelect>
          </div>
        </div>
        <ColorChannelEditor
          colorSpace={property.colorSpace}
          color={property.color}
          controlProps={controlProps}
          overrideActiveFields={overrideActiveFields}
          onColorSpaceChange={colorSpace => onChange(animationPropertyWithColorSpace(property, colorSpace))}
          onColorChange={color => onChange({ ...property, color, channels: allColorAnimationChannels(property.colorSpace) })}
          onChannelChange={(channel, value) => onChange(animationPropertyWithColorChannelValue(property, channel, value))}
        />
      </div>
    );
  }

  if ("value" in property) {
    return (
      <NodeMetric label="Value" value={property.value} overridden={overrideActiveFields.has("value")} onCommit={value => onChange({ ...property, value })} onFocus={controlProps.onFocus} />
    );
  }

  return <div className="text-[11px] text-slate-500">No numeric input</div>;
}

type GroupOverrideMode = "none" | AnimationGroupNumericOverride["kind"];

type NumericPropertyField = {
  key: string;
  label: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
};

const GROUP_OVERRIDE_MODE_OPTIONS: Array<{ value: GroupOverrideMode; label: string }> = [
  { value: "none", label: "None" },
  { value: "distributed", label: "Distributed" },
  { value: "random", label: "Random" },
  { value: "list", label: "From list" },
];

function GroupBehaviorEditor({
  property,
  expanded,
  controlProps,
  onExpandedChange,
  onChange,
}: {
  property: AnimationProperty;
  expanded: boolean;
  controlProps: ControlProps;
  onExpandedChange: (expanded: boolean) => void;
  onChange: (property: AnimationProperty) => void;
}) {
  const fields = numericPropertyFields(property);
  if (fields.length === 0) {
    return null;
  }
  const hasOverrides = activeGroupOverrideFieldKeys(property).size > 0;
  return (
    <div className="space-y-1">
      <button
        type="button"
        className={`flex h-5 items-center gap-1 rounded-sm px-0.5 text-[10px] ${
          hasOverrides && !expanded
            ? "text-sky-300 hover:text-sky-200"
            : "text-slate-500 hover:text-slate-300"
        }`}
        onPointerDown={event => {
          controlProps.onFocus();
          event.stopPropagation();
        }}
        onClick={event => {
          event.stopPropagation();
          onExpandedChange(!expanded);
        }}
      >
        {expanded ? <ChevronDown className="size-3" aria-hidden="true" /> : <ChevronRight className="size-3" aria-hidden="true" />}
        <span>Group behavior</span>
      </button>
      {expanded ? (
        <div className="space-y-2 border-l border-white/10 pl-2">
          {fields.map(field => {
            const override = property.groupOverrides?.[field.key];
            const mode: GroupOverrideMode = override?.kind ?? "none";
            return (
              <div key={field.key} className="space-y-1.5">
                <div className="grid grid-cols-[minmax(0,1fr)_112px] items-end gap-2">
                  <div className="truncate text-[11px] text-slate-500">{field.label}</div>
                  <NodeSelect
                    value={mode}
                    options={GROUP_OVERRIDE_MODE_OPTIONS}
                    onChange={nextMode => onChange(propertyWithGroupOverrideMode(property, field, nextMode))}
                    onFocus={controlProps.onFocus}
                  />
                </div>
                {override?.kind === "distributed" ? (
                  <div className="grid grid-cols-2 gap-2">
                    <NodeMetric label="From" value={override.from} min={field.min} max={field.max} step={field.step} onCommit={from => onChange(propertyWithGroupOverride(property, field.key, { ...override, from }))} onFocus={controlProps.onFocus} />
                    <NodeMetric label="To" value={override.to} min={field.min} max={field.max} step={field.step} onCommit={to => onChange(propertyWithGroupOverride(property, field.key, { ...override, to }))} onFocus={controlProps.onFocus} />
                  </div>
                ) : null}
                {override?.kind === "random" ? (
                  <div className="grid grid-cols-3 gap-2">
                    <NodeMetric label="From" value={override.from} min={field.min} max={field.max} step={field.step} onCommit={from => onChange(propertyWithGroupOverride(property, field.key, { ...override, from }))} onFocus={controlProps.onFocus} />
                    <NodeMetric label="To" value={override.to} min={field.min} max={field.max} step={field.step} onCommit={to => onChange(propertyWithGroupOverride(property, field.key, { ...override, to }))} onFocus={controlProps.onFocus} />
                    <NodeMetric label="Seed" value={override.seed} step={1} onCommit={seed => onChange(propertyWithGroupOverride(property, field.key, { ...override, seed: Math.round(seed) }))} onFocus={controlProps.onFocus} />
                  </div>
                ) : null}
                {override?.kind === "list" ? (
                  <div className="space-y-1">
                    <input
                      value={override.values}
                      onChange={event => onChange(propertyWithGroupOverride(property, field.key, { ...override, values: event.currentTarget.value }))}
                      onPointerDownCapture={controlProps.onFocus}
                      onPointerDown={event => event.stopPropagation()}
                      onFocus={controlProps.onFocus}
                      className="h-7 w-full rounded border border-white/10 bg-black/20 px-2 text-[11px] text-slate-200 outline-none focus:border-sky-400/60"
                    />
                    <div className="min-h-4 truncate text-[10px] text-slate-500">
                      Parsed: {parseGroupOverrideList(override.values).join(", ") || "none"}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function propertyWithGroupOverrideMode(property: AnimationProperty, field: NumericPropertyField, mode: GroupOverrideMode): AnimationProperty {
  if (mode === "none") {
    return propertyWithGroupOverride(property, field.key, undefined);
  }
  if (mode === "distributed") {
    return propertyWithGroupOverride(property, field.key, { kind: "distributed", from: field.value, to: field.value });
  }
  if (mode === "random") {
    return propertyWithGroupOverride(property, field.key, { kind: "random", from: field.value, to: field.value, seed: 42 });
  }
  return propertyWithGroupOverride(property, field.key, { kind: "list", values: formatGroupOverrideValue(field.value) });
}

function propertyWithGroupOverride(property: AnimationProperty, fieldKey: string, override: AnimationGroupNumericOverride | undefined): AnimationProperty {
  const groupOverrides = { ...(property.groupOverrides ?? {}) };
  if (override) {
    groupOverrides[fieldKey] = override;
  } else {
    delete groupOverrides[fieldKey];
  }
  return Object.keys(groupOverrides).length > 0
    ? { ...property, groupOverrides }
    : propertyWithoutGroupOverrides(property);
}

function propertyWithoutGroupOverrides(property: AnimationProperty): AnimationProperty {
  const { groupOverrides: _groupOverrides, ...rest } = property;
  return rest as AnimationProperty;
}

function activeGroupOverrideFieldKeys(property: AnimationProperty): Set<string> {
  return new Set(Object.keys(property.groupOverrides ?? {}));
}

function numericPropertyFields(property: AnimationProperty): NumericPropertyField[] {
  if (property.kind === "pin") {
    if (property.target?.kind === "pathPercent") {
      return [{ key: "target.progress", label: "Path %", value: property.target.progress * 100, min: 0, max: 100, step: 0.1 }];
    }
    if (isDistancePinTarget(property.target)) {
      return [{
        key: "target.distance",
        label: distancePinMetricLabel(property.target.kind),
        value: property.target.distance,
        min: 0,
      }];
    }
    return [
      { key: "x", label: pinAxisLabel(property, "x"), value: property.x },
      { key: "y", label: pinAxisLabel(property, "y"), value: property.y },
    ];
  }
  if (property.kind === "followPath" || property.kind === "strokePath") {
    return [{ key: "progress", label: "To %", value: property.progress * 100, min: 0, max: 100, step: 0.1 }];
  }
  if (property.kind === "opacity") {
    return [{ key: "value", label: "%", value: opacityToPercent(property.value), min: 0, max: 100 }];
  }
  if (property.kind === "layout" || property.kind === "stack" || property.kind === "inline") {
    return [{ key: "gap", label: "Gap", value: property.gap }];
  }
  if (property.kind === "fitToBBox") {
    return [{ key: "pad", label: "Pad", value: property.pad }];
  }
  if (property.kind === "orbit") {
    return [{ key: "angle", label: "Deg", value: property.angle }];
  }
  if (property.kind === "rotation") {
    return [{ key: "value", label: "Deg", value: property.value }];
  }
  if (property.kind === "effectValue") {
    return [{ key: "value", label: property.field === "opacity" ? "%" : "px", value: property.value }];
  }
  if (property.kind === "fillColor" || property.kind === "strokeColor" || property.kind === "effectColor") {
    return colorAnimationChannelFields(property.colorSpace, property.color).map(field => ({
      key: field.key,
      label: field.label,
      value: field.value,
      min: field.min,
      max: field.max,
      step: field.step,
    }));
  }
  if ("value" in property) {
    return [{ key: "value", label: "Value", value: property.value }];
  }
  return [];
}

function parseGroupOverrideList(values: string): number[] {
  return values
    .split(",")
    .map(value => Number.parseFloat(value.trim()))
    .filter(Number.isFinite);
}

function formatGroupOverrideValue(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(3)));
}

function NodeLabeledSelect<T extends string>({
  label,
  value,
  options,
  onChange,
  onFocus,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
  onFocus: () => void;
}) {
  return (
    <label className="space-y-1">
      <span className="text-[11px] text-slate-500">{label}</span>
      <NodeSelect value={value} options={options} onChange={onChange} onFocus={onFocus} />
    </label>
  );
}

function NodeSelect<T extends string>({
  value,
  options,
  onChange,
  onFocus,
  className = "w-full",
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
  onFocus: () => void;
  className?: string;
}) {
  return (
    <div
      className={className}
      onPointerDownCapture={onFocus}
      onPointerDown={event => event.stopPropagation()}
      onFocusCapture={onFocus}
    >
      <PanelSelect
        value={value}
        onValueChange={onChange}
        triggerClassName={nodeSelectTriggerClass}
        contentClassName={nodeSelectContentClass}
        contentPosition={nodeSelectContentPosition}
        contentAvoidCollisions={false}
      >
        {options.map(option => (
          <PanelSelectItem key={option.value} value={option.value}>
            {option.label}
          </PanelSelectItem>
        ))}
      </PanelSelect>
    </div>
  );
}

function NodeMetric({
  label,
  value,
  min,
  max,
  step,
  disabled = false,
  overridden = false,
  onCommit,
  onFocus,
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  overridden?: boolean;
  onCommit: (value: number) => void;
  onFocus: () => void;
}) {
  return (
    <div
      className={`${disabled ? "opacity-45" : ""} ${overridden ? "[&_input]:text-slate-500 [&_input]:line-through" : ""}`}
      onPointerDownCapture={onFocus}
      onPointerDown={event => event.stopPropagation()}
      onFocusCapture={onFocus}
    >
      <Metric
        label={label}
        value={value}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        onCommit={onCommit}
      />
    </div>
  );
}

function NodeToggle({
  label,
  checked,
  onChange,
  onFocus,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  onFocus: () => void;
}) {
  return (
    <button
      type="button"
      className={`flex h-7 w-full min-w-0 items-center justify-between gap-2 rounded border px-2 text-[11px] ${
        checked
          ? "border-sky-300/35 bg-sky-400/15 text-sky-100"
          : "border-white/10 bg-white/[0.04] text-slate-400 hover:bg-white/[0.08] hover:text-slate-200"
      }`}
      onPointerDown={event => {
        onFocus();
        event.stopPropagation();
      }}
      onClick={event => {
        event.stopPropagation();
        onChange(!checked);
      }}
    >
      <span className="truncate">{label}</span>
      <span className={`h-3.5 w-6 shrink-0 rounded-full border p-0.5 ${checked ? "border-sky-200/50 bg-sky-300/30" : "border-white/10 bg-black/20"}`}>
        <span className={`block size-2 rounded-full ${checked ? "translate-x-2.5 bg-sky-100" : "bg-slate-500"}`} />
      </span>
    </button>
  );
}

function opacityToPercent(value: number): number {
  return Math.max(0, Math.min(100, (value > 1 ? value / 100 : value) * 100));
}

function propertyOptionKind(property: AnimationProperty): AnimatablePropertyKind {
  switch (property.kind) {
    case "opacity":
    case "scale":
    case "scaleX":
    case "scaleY":
    case "rotation":
    case "width":
    case "height":
    case "stack":
    case "inline":
    case "fitToBBox":
    case "followPath":
    case "strokePath":
    case "orbit":
    case "lookAt":
      return property.kind;
    case "pin":
      return "position";
    case "layout":
      return property.axis === "y" ? "layoutY" : "layoutX";
    case "prop":
      if (property.name === "strokeWidth") {
        return "strokeWidth";
      }
      return "cornerRadius";
    case "fillColor":
      return "fill";
    case "strokeColor":
      return "stroke";
    case "effectValue":
    case "effectColor":
      return "effect";
  }
}

const DEFAULT_ANIM_COLOR: OklchColor = { l: 0.7, c: 0.15, h: 250, alpha: 1 };
const DEFAULT_ANIM_CHANNELS: string[] = ["l", "c", "h", "alpha"];

function propertyForOption(kind: AnimatablePropertyKind, id = createId("animProp")): AnimationProperty {
  switch (kind) {
    case "opacity":
      return { id, kind, value: 1 };
    case "position":
      return { id, kind: "pin", anchor: "center", x: 0, y: 0 };
    case "scale":
    case "scaleX":
    case "scaleY":
      return { id, kind, value: 1 };
    case "rotation":
      return { id, kind, value: 0 };
    case "width":
      return { id, kind, value: 100 };
    case "height":
      return { id, kind, value: 100 };
    case "layoutX":
      return { id, kind: "layout", axis: "x", gap: 8 };
    case "layoutY":
      return { id, kind: "layout", axis: "y", gap: 8 };
    case "stack":
      return { id, kind: "stack", gap: 8 };
    case "inline":
      return { id, kind: "inline", gap: 8 };
    case "fitToBBox":
      return { id, kind: "fitToBBox", pad: 0 };
    case "followPath":
      return { id, kind: "followPath", anchor: "center", progress: 1, orientToPath: false };
    case "strokePath":
      return { id, kind: "strokePath", inverse: false, erase: false, progress: 1 };
    case "orbit":
      return { id, kind: "orbit", angle: 360 };
    case "lookAt":
      return { id, kind: "lookAt" };
    case "cornerRadius":
      return { id, kind: "prop", name: "cornerRadius", value: 12 };
    case "strokeWidth":
      return { id, kind: "prop", name: "strokeWidth", value: 2 };
    case "fill":
      return { id, kind: "fillColor", fillIndex: 0, colorSpace: "oklch", channels: DEFAULT_ANIM_CHANNELS, color: DEFAULT_ANIM_COLOR };
    case "stroke":
      return { id, kind: "strokeColor", strokeIndex: 0, colorSpace: "oklch", channels: DEFAULT_ANIM_CHANNELS, color: DEFAULT_ANIM_COLOR };
    case "effect":
      return { id, kind: "effectValue", effectIndex: 0, field: "blur", value: 4 };
  }
}

function distancePinMetricLabel(kind: "distance" | "minDistance" | "maxDistance"): string {
  if (kind === "minDistance") {
    return "Min d";
  }
  if (kind === "maxDistance") {
    return "Max d";
  }
  return "Distance";
}

function pinTargetOption(property: Extract<AnimationProperty, { kind: "pin" }>): PinTargetOption {
  if (property.target?.kind === "frameAnchor") {
    return `frame:${property.target.anchor}`;
  }
  if (property.target?.kind === "pathPercent") {
    return "pathPercent";
  }
  if (property.target?.kind === "offset") {
    return "offset";
  }
  if (isDistancePinTarget(property.target)) {
    return property.target.kind;
  }
  return "point";
}

function pinWithTargetOption(property: Extract<AnimationProperty, { kind: "pin" }>, option: PinTargetOption): Extract<AnimationProperty, { kind: "pin" }> {
  if (option === "point") {
    return { ...property, target: undefined };
  }
  if (option === "offset") {
    return { ...property, target: { kind: "offset" } };
  }
  if (option === "distance" || option === "minDistance" || option === "maxDistance") {
    const previousDistance = isDistancePinTarget(property.target) ? property.target.distance : 100;
    const previousSnapBack = isClampedDistancePinTarget(property.target)
      ? distancePinSnapBack(property.target)
      : false;
    if (option === "distance") {
      return {
        ...property,
        anchor: "center",
        target: { kind: "distance", distance: previousDistance },
      };
    }
    return {
      ...property,
      anchor: "center",
      target: previousSnapBack
        ? { kind: option, distance: previousDistance, snapBack: true }
        : { kind: option, distance: previousDistance },
    };
  }
  if (option === "pathPercent") {
    return {
      ...property,
      target: { kind: "pathPercent", progress: property.target?.kind === "pathPercent" ? property.target.progress : 1 },
    };
  }
  return {
    ...property,
    target: { kind: "frameAnchor", anchor: option.slice("frame:".length) as AnimationAnchor },
  };
}

function pinAxisLabel(property: Extract<AnimationProperty, { kind: "pin" }>, axis: "x" | "y"): string {
  if (property.target?.kind === "frameAnchor" || property.target?.kind === "offset") {
    return axis === "x" ? "dX" : "dY";
  }
  return axis === "x" ? "X" : "Y";
}

function isOutputPin(pin: AnimationPinKind): boolean {
  return pin === "output" || pin === "motionOutput" || pin === "motionSequenceOutput";
}

function isMotionSequenceOutput(node: AnimationGraphNode, pin: AnimationPinKind): boolean {
  return (node.kind === "animation" && pin === "output") || pin === "motionSequenceOutput";
}

function canDragCreateMotionNode(node: AnimationGraphNode, pin: AnimationPinKind): boolean {
  return (node.kind === "animate" && pin === "output") || isMotionSequenceOutput(node, pin);
}

function topCenterNodePosition(point: { x: number; y: number }): { x: number; y: number } {
  return { x: point.x - NODE_WIDTH / 2, y: point.y };
}

function nearestInputPin(graph: AnimationGraph, point: { x: number; y: number }, sourceNodeId: string, preview?: Map<string, { x: number; y: number }>, document?: CurvaDocument): AnimationEndpoint | undefined {
  let best: { endpoint: AnimationEndpoint; distance: number } | undefined;
  for (const node of graph.nodes) {
    if (node.id === sourceNodeId) {
      continue;
    }
    for (const pin of pinsForNode(node, graph)) {
      if (isOutputPin(pin.pin) || !isPinVisibleOnNode(node, pin.pin)) {
        continue;
      }
      const pinDoc = pinPoint(node, { nodeId: node.id, pin: pin.pin, index: pin.index }, preview, graph, document);
      const distance = Math.hypot(point.x - pinDoc.x, point.y - pinDoc.y);
      if (distance < 18 && (!best || distance < best.distance)) {
        best = { endpoint: { nodeId: node.id, pin: pin.pin, index: pin.index }, distance };
      }
    }
  }
  return best?.endpoint;
}

export function nearestAnimationObjectInputPin(nodes: AnimationGraphNode[], point: { x: number; y: number }): AnimationEndpoint | undefined {
  let best: { endpoint: AnimationEndpoint; distance: number } | undefined;
  for (const node of nodes) {
    for (const pin of pinsForNode(node)) {
      if (!objectInputPinsForNode(node).includes(pin.pin) || !isPinVisibleOnNode(node, pin.pin)) {
        continue;
      }
      const pinDoc = pinPoint(node, { nodeId: node.id, pin: pin.pin, index: pin.index });
      const distance = Math.hypot(point.x - pinDoc.x, point.y - pinDoc.y);
      if (distance < 18 && (!best || distance < best.distance)) {
        best = { endpoint: { nodeId: node.id, pin: pin.pin, index: pin.index }, distance };
      }
    }
  }
  return best?.endpoint;
}

function objectInputPinsForNode(node: AnimationGraphNode): AnimationPinKind[] {
  if (node.kind === "group") {
    return ["input"];
  }
  if (node.kind === "animate") {
    return ["input", "dependencyInput"];
  }
  return [];
}

function animationObjectLinks(graph: AnimationGraph): Array<{ target: { kind: "sceneNode"; nodeId: string } | import("@/document/model/animation").VectorSubElementAnimationTarget; endpoint: AnimationEndpoint }> {
  type SceneNodeTarget = { kind: "sceneNode"; nodeId: string };
  type LinkTarget = SceneNodeTarget | import("@/document/model/animation").VectorSubElementAnimationTarget;
  return graph.nodes.flatMap((node): Array<{ target: LinkTarget; endpoint: AnimationEndpoint }> => {
    if (node.kind === "group") {
      return node.targets.flatMap((target): Array<{ target: LinkTarget; endpoint: AnimationEndpoint }> => {
        if (target.kind === "sceneNode" || target.kind === "vectorVertex" || target.kind === "vectorEdge" || target.kind === "vectorHandle") {
          return [{ target: target as LinkTarget, endpoint: { nodeId: node.id, pin: "input" as const } }];
        }
        return [];
      });
    }
    if (node.kind === "animate") {
      const links: Array<{ target: LinkTarget; endpoint: AnimationEndpoint }> = [];
      if (node.target && (node.target.kind === "sceneNode" || node.target.kind === "vectorVertex" || node.target.kind === "vectorEdge" || node.target.kind === "vectorHandle")) {
        links.push({ target: node.target as LinkTarget, endpoint: { nodeId: node.id, pin: "input" } });
      }
      for (const [index, property] of node.properties.entries()) {
        if (property.kind === "fitToBBox" && property.target?.kind === "sceneNode") {
          links.push({ target: property.target, endpoint: { nodeId: node.id, pin: "dependencyInput", index } });
        } else if (property.kind === "pin" && property.dependencyTarget?.kind === "sceneNode") {
          links.push({ target: property.dependencyTarget, endpoint: { nodeId: node.id, pin: "dependencyInput", index } });
        } else if (property.kind === "followPath" && property.pathTarget?.kind === "sceneNode") {
          links.push({ target: property.pathTarget, endpoint: { nodeId: node.id, pin: "dependencyInput", index } });
        } else if (property.kind === "orbit" && property.target?.kind === "sceneNode") {
          links.push({ target: property.target, endpoint: { nodeId: node.id, pin: "dependencyInput", index } });
        } else if (property.kind === "lookAt" && property.target?.kind === "sceneNode") {
          links.push({ target: property.target, endpoint: { nodeId: node.id, pin: "dependencyInput", index } });
        }
      }
      return links;
    }
    return [];
  });
}

function objectIdsForEndpoint(graph: AnimationGraph, endpoint: AnimationEndpoint): Set<string> {
  return new Set(
    animationObjectLinks(graph)
      .filter(link => endpointsEqual(link.endpoint, endpoint))
      .map(link => link.target.nodeId),
  );
}

function endpointsEqual(left: AnimationEndpoint, right: AnimationEndpoint): boolean {
  return left.nodeId === right.nodeId && left.pin === right.pin && (left.index ?? 0) === (right.index ?? 0);
}

function hitTestAnimationObject(document: CurvaDocument, point: { x: number; y: number }, zoom: number, deepSelect: boolean): string | undefined {
  const hit = hitTestDocument(document, point, undefined, zoom, { coarseGeometry: true, deepSelect });
  return hit.type === "node" ? hit.nodeId : undefined;
}

function isSelectableAnimationObject(node: SceneNode): boolean {
  return node.visible;
}

function nodeTypeLabel(node: SceneNode): string {
  if (node.type === "primitive") {
    return node.primitive.kind;
  }
  if (node.type === "vectorNetwork") {
    return "vector";
  }
  return node.type;
}

function animationTargetLabel(graph: AnimationGraph, frames: FrameNode[], nodeId: string | undefined): string {
  if (!nodeId) {
    return "Select animation";
  }
  const node = graph.nodes.find(candidate => candidate.id === nodeId);
  if (node?.kind !== "animation") {
    return "Missing animation";
  }
  const frame = frames.find(candidate => candidate.id === node.frameId);
  return frame ? `${node.name} · ${frame.name}` : node.name;
}

function floatingPickerStyle(position: { x: number; y: number }, width: number, maxHeight: number): { left: number; top: number; maxHeight: number } {
  const gap = 8;
  const viewportWidth = typeof window === "undefined" ? width + gap * 2 : window.innerWidth;
  const viewportHeight = typeof window === "undefined" ? maxHeight + gap * 2 : window.innerHeight;
  return {
    left: Math.max(gap, Math.min(position.x, viewportWidth - width - gap)),
    top: Math.max(gap, Math.min(position.y, viewportHeight - maxHeight - gap)),
    maxHeight,
  };
}

function nodeCenter(node: { bounds: { x: number; y: number; width: number; height: number } }): { x: number; y: number } {
  return {
    x: node.bounds.x + node.bounds.width / 2,
    y: node.bounds.y + node.bounds.height / 2,
  };
}

function vectorSubElementPoint(
  document: CurvaDocument,
  target: import("@/document/model/animation").AnimationTarget,
  sourceNode: SceneNode,
): { x: number; y: number } | undefined {
  if (target.kind === "sceneNode") return undefined;
  if (sourceNode.type !== "vectorNetwork") return undefined;

  const network = sourceNode.network;
  if (target.kind === "vectorVertex") {
    const vertex = network.vertices.find(v => v.id === target.vertexId);
    if (!vertex) return undefined;
    return nodeSelectionPointToWorld(document, sourceNode.id, { x: vertex.x, y: vertex.y });
  }
  if (target.kind === "vectorEdge") {
    const edge = network.edges.find(e => e.id === target.edgeId);
    if (!edge) return undefined;
    const fromVertex = network.vertices.find(v => v.id === edge.from.vertexId);
    const toVertex = network.vertices.find(v => v.id === edge.to.vertexId);
    if (!fromVertex || !toVertex) return undefined;
    const midLocal = { x: (fromVertex.x + toVertex.x) / 2, y: (fromVertex.y + toVertex.y) / 2 };
    return nodeSelectionPointToWorld(document, sourceNode.id, midLocal);
  }
  if (target.kind === "vectorHandle") {
    const edge = network.edges.find(e => e.id === target.edgeId);
    if (!edge) return undefined;
    const endpointData = target.endpoint === "from" ? edge.from : edge.to;
    if (endpointData.handle) {
      const anchorVertex = network.vertices.find(v => v.id === endpointData.vertexId);
      if (!anchorVertex) return undefined;
      return nodeSelectionPointToWorld(document, sourceNode.id, {
        x: anchorVertex.x + endpointData.handle.x,
        y: anchorVertex.y + endpointData.handle.y,
      });
    }
    const anchorVertex = network.vertices.find(v => v.id === endpointData.vertexId);
    if (!anchorVertex) return undefined;
    return nodeSelectionPointToWorld(document, sourceNode.id, { x: anchorVertex.x, y: anchorVertex.y });
  }
  return undefined;
}

function nodeRect(node: AnimationGraphNode, preview?: Map<string, { x: number; y: number }>, graph?: AnimationGraph, document?: CurvaDocument) {
  const position = preview?.get(node.id) ?? node.position;
  return { x: position.x, y: position.y, width: NODE_WIDTH, height: nodeHeight(node, graph, document) };
}

function visibleNodeRect(node: AnimationGraphNode, preview?: Map<string, { x: number; y: number }>, graph?: AnimationGraph, document?: CurvaDocument) {
  const rect = nodeRect(node, preview, graph, document);
  const position = preview?.get(node.id) ?? node.position;
  const pinRects = pinsForNode(node, graph)
    .filter(pin => isPinVisibleOnNode(node, pin.pin))
    .map(pin => {
      const point = localPinPoint(node, { nodeId: node.id, pin: pin.pin, index: pin.index }, graph, document);
      return {
        x: position.x + point.x - PIN_RADIUS,
        y: position.y + point.y - PIN_RADIUS,
        width: PIN_RADIUS * 2,
        height: PIN_RADIUS * 2,
      };
    });
  return pinRects.length === 0 ? rect : unionRects([rect, ...pinRects]);
}

function unionRects(rects: Array<{ x: number; y: number; width: number; height: number }>): { x: number; y: number; width: number; height: number } {
  const left = Math.min(...rects.map(rect => rect.x));
  const top = Math.min(...rects.map(rect => rect.y));
  const right = Math.max(...rects.map(rect => rect.x + rect.width));
  const bottom = Math.max(...rects.map(rect => rect.y + rect.height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

function visualGroupBadgePoint(bounds: { x: number; y: number; width: number; height: number }, dir: Dir): { x: number; y: number } {
  const width = Math.min(96, Math.max(64, bounds.width));
  const height = 20;
  const left = bounds.x;
  const top = bounds.y - VISUAL_GROUP_LABEL_OFFSET_PX;
  const center = { x: left + width / 2, y: top + height / 2 };
  if (Math.abs(dir.dx) >= Math.abs(dir.dy) && dir.dx !== 0) {
    return { x: dir.dx > 0 ? left + width : left, y: center.y };
  }
  if (dir.dy !== 0) {
    return { x: center.x, y: dir.dy > 0 ? top + height : top };
  }
  return center;
}

export function animationNodesInRect(nodes: AnimationGraphNode[], rect: { x: number; y: number; width: number; height: number }): string[] {
  return nodes.filter(node => rectIntersects(rect, nodeRect(node))).map(node => node.id);
}

function pinPoint(node: AnimationGraphNode, endpoint: AnimationEndpoint, preview?: Map<string, { x: number; y: number }>, graph?: AnimationGraph, document?: CurvaDocument) {
  const position = preview?.get(node.id) ?? node.position;
  const local = localPinPoint(node, endpoint, graph, document);
  return { x: position.x + local.x, y: position.y + local.y };
}

function localPinPoint(node: AnimationGraphNode, endpoint: AnimationEndpoint, graph?: AnimationGraph, document?: CurvaDocument) {
  const height = nodeHeight(node, graph, document);
  if (isAnimationNodeCollapsed(node)) {
    return localCollapsedNodePinPoint(node, endpoint, height);
  }
  switch (endpoint.pin) {
    case "input":
    case "objectInput":
      return { x: 0, y: height / 2 };
    case "dependencyInput":
      return { x: 20, y: dependencyPinY(node, endpoint.index) ?? Math.min(height - 24, height / 2 + 32) };
    case "motionInput":
      return { x: 0, y: 46 + (endpoint.index ?? 0) * 22 };
    case "motionSequenceInput":
      return { x: NODE_WIDTH / 2, y: 0 };
    case "motionSequenceOutput":
      return { x: NODE_WIDTH / 2, y: height };
    case "output":
      return node.kind === "animation"
        ? { x: NODE_WIDTH / 2, y: height }
        : { x: NODE_WIDTH, y: height / 2 };
    case "motionOutput":
      return { x: NODE_WIDTH, y: height / 2 };
  }
}

function localCollapsedNodePinPoint(node: AnimationGraphNode, endpoint: AnimationEndpoint, height: number) {
  switch (endpoint.pin) {
    case "motionSequenceInput":
      return { x: NODE_WIDTH / 2, y: 0 };
    case "motionSequenceOutput":
      return { x: NODE_WIDTH / 2, y: height };
    case "output":
      return node.kind === "animation"
        ? { x: NODE_WIDTH / 2, y: height }
        : { x: NODE_WIDTH, y: height / 2 };
    case "motionOutput":
      return { x: NODE_WIDTH, y: height / 2 };
    default:
      return { x: 0, y: height / 2 };
  }
}

function nodeHeight(node: AnimationGraphNode, graph?: AnimationGraph, document?: CurvaDocument): number {
  if (isAnimationNodeCollapsed(node)) {
    return COLLAPSED_NODE_HEIGHT;
  }
  switch (node.kind) {
    case "animation":
      // header(36) + py-3(24) + frame-select(28) = 88
      return 88;
    case "group":
      const layout = groupNodeLayout(node, document);
      // header(36) + py-3(24) + target list + optional regroup select
      return 60
        + Math.max(34, layout.rowCount * 32)
        + (layout.canRegroupText ? 52 : 0)
        + (layout.hasRegroupActions ? 70 : 0);
    case "animate":
      // header(36) + py-3(24) + add-button-row(36) + per-property
      return 96 + node.properties.reduce((height, property) => height + propertyRowHeight(property), 0);
    case "motion": {
      const chromeHeight = 60; // header(36) + body py-3(24)
      const baseControlsHeight = 68; // pt-1(4) + duration row(28) + gap(8) + transition toggle(28)
      const independentToggleHeight = motionHasGroupInput(graph, node) ? 36 : 0;
      const stutterControlHeight = motionCanStutter(graph, node) ? 36 : 0;
      const transitionEditorHeight = node.transition
        ? 8 + (node.transitionEffect === "fadeThroughColor" ? 138 : 85)
        : 0;
      const easingEditorHeight = node.durationMs > 0
        ? 8 + (node.easing.kind === "wiggle" ? 211 : 139)
        : 0;
      const motionInputHeight = (node.durationMs <= 0 ? 104 : 194) + motionInputIndexes(graph, node.id).length * 22;
      return Math.max(chromeHeight + baseControlsHeight + independentToggleHeight + stutterControlHeight + transitionEditorHeight + easingEditorHeight, motionInputHeight);
    }
  }
}

function groupNodeLayout(node: GroupAnimationNode, document?: CurvaDocument): { canRegroupText: boolean; rowCount: number; hasRegroupActions: boolean } {
  const target = node.targets.length === 1 ? node.targets[0] : undefined;
  const singleTargetNode = document && target?.kind === "sceneNode"
    ? findSceneNode(document, target.nodeId)
    : undefined;
  const canRegroupText = document ? singleTargetNode?.type === "text" : node.targets.length === 1;
  if (!canRegroupText || node.regroup === "none") {
    return { canRegroupText, rowCount: node.targets.length, hasRegroupActions: false };
  }
  const fragmentCount = singleTargetNode?.type === "text"
    ? splitTextNodeForRegroup(singleTargetNode, node.regroup).length
    : node.regroupOrder?.length ?? 6;
  const rowCount = Math.min(Math.max(fragmentCount, 1), 6);
  return { canRegroupText, rowCount, hasRegroupActions: fragmentCount > 1 };
}

function propertyHasDependencyInput(property: AnimationProperty): boolean {
  return property.kind === "fitToBBox"
    || property.kind === "pin"
    || property.kind === "followPath"
    || property.kind === "orbit"
    || property.kind === "lookAt";
}

function propertyRowHeight(property: AnimationProperty): number {
  const groupBehaviorHeight = numericPropertyFields(property).length > 0 ? 24 : 0;
  switch (property.kind) {
    case "pin":
      return 132 + groupBehaviorHeight;
    case "followPath":
      return 132 + groupBehaviorHeight;
    case "strokePath":
      return 116 + groupBehaviorHeight;
    case "fillColor":
    case "strokeColor":
    case "effectColor":
      return 168 + groupBehaviorHeight;
    case "effectValue":
      return 116 + groupBehaviorHeight;
    default:
      return 84 + groupBehaviorHeight;
  }
}

function dependencyPinY(node: AnimationGraphNode, index: number | undefined): number | undefined {
  if (node.kind !== "animate" || index === undefined || index < 0 || index >= node.properties.length) {
    return undefined;
  }
  let y = 36 + 12;
  for (let i = 0; i < index; i += 1) {
    y += propertyRowHeight(node.properties[i]!);
  }
  return y + propertyRowHeight(node.properties[index]!) / 2;
}

type ConnectionColor = "default" | "object" | "dependency" | "sequence" | "motion";

function connectionColorForGraphConnection(connection: AnimationConnection, nodeById: Map<string, AnimationGraphNode>): ConnectionColor {
  const fromNode = nodeById.get(connection.from.nodeId);
  const toNode = nodeById.get(connection.to.nodeId);
  if (connection.from.pin === "motionSequenceOutput" || connection.to.pin === "motionSequenceInput" || (fromNode?.kind === "animation" && connection.from.pin === "output")) {
    return "sequence";
  }
  if (connection.from.pin === "output" && fromNode?.kind === "animate" && connection.to.pin === "motionInput") {
    return "motion";
  }
  if (connection.to.pin === "dependencyInput") {
    return "dependency";
  }
  if (connectionColorForEndpoint(connection.from, fromNode) === "motion" || connectionColorForEndpoint(connection.to, toNode) === "motion") {
    return "motion";
  }
  return "default";
}

function connectionColorForEndpoint(endpoint: AnimationEndpoint, node: AnimationGraphNode | undefined): ConnectionColor {
  if ((node?.kind === "animation" && endpoint.pin === "output") || endpoint.pin === "motionSequenceInput" || endpoint.pin === "motionSequenceOutput") {
    return "sequence";
  }
  if ((node?.kind === "animate" && endpoint.pin === "output") || endpoint.pin === "motionInput") {
    return "motion";
  }
  if (endpoint.pin === "dependencyInput") {
    return "dependency";
  }
  return "default";
}

function connectionStrokeClass(selected: boolean, color: ConnectionColor | boolean, kind: "graph" | "object"): string {
  const resolvedColor: ConnectionColor = typeof color === "boolean"
    ? color ? "dependency" : kind === "object" ? "object" : "default"
    : color;
  if (resolvedColor === "dependency") {
    return selected ? "stroke-pink-300" : "stroke-pink-400/75";
  }
  if (resolvedColor === "sequence") {
    return selected ? "stroke-emerald-300" : "stroke-emerald-400/75";
  }
  if (resolvedColor === "motion") {
    return selected ? "stroke-purple-300" : "stroke-purple-400/75";
  }
  if (resolvedColor === "object") {
    return selected ? "stroke-sky-300" : "stroke-sky-400/70";
  }
  return selected ? "stroke-sky-300" : "stroke-slate-300/70";
}

function previewConnectionStrokeClass(color: ConnectionColor): string {
  switch (color) {
    case "dependency":
      return "stroke-pink-400";
    case "sequence":
      return "stroke-emerald-400";
    case "motion":
      return "stroke-purple-400";
    case "object":
      return "stroke-sky-400";
    case "default":
      return "stroke-sky-400";
  }
}

function pinTitle(node: AnimationGraphNode, pin: AnimationPinKind, index: number | undefined): string {
  if (node.kind === "animate" && pin === "dependencyInput") {
    const property = index === undefined ? undefined : node.properties[index];
    if (property?.kind === "pin") {
      return "Position bbox target";
    }
    if (property?.kind === "fitToBBox") {
      return "Fit to BBox target";
    }
    if (property?.kind === "followPath") {
      return "Path target";
    }
    if (property?.kind === "orbit") {
      return "Orbit target";
    }
    if (property?.kind === "lookAt") {
      return "Look At target";
    }
    return "Dependency target";
  }
  if (node.kind === "animate" && pin === "input") {
    return "Behavior target";
  }
  return pin;
}

function motionInputIndexes(graph: AnimationGraph | undefined, nodeId: string): number[] {
  const indexes = new Set(
    graph?.connections
      .filter(connection => connection.to.nodeId === nodeId && connection.to.pin === "motionInput")
      .map(connection => connection.to.index ?? 0) ?? [],
  );
  if (indexes.size === 0) {
    indexes.add(0);
  }
  return [...indexes].sort((left, right) => left - right);
}

function motionCanStutter(graph: AnimationGraph | undefined, node: MotionAnimationNode): boolean {
  if (!graph) {
    return false;
  }
  const animateNodes = animateNodesForMotionInputOrder(graph, node.id);
  if (animateNodes.length > 1) {
    return true;
  }
  const singleNode = animateNodes[0];
  return singleNode ? animateNodeGroupTargetCount(graph, singleNode) > 1 : false;
}

function motionHasGroupInput(graph: AnimationGraph | undefined, node: MotionAnimationNode): boolean {
  if (!graph) {
    return false;
  }
  return animateNodesForMotionInputOrder(graph, node.id).some(animateNode => animateNodeHasGroupInput(graph, animateNode));
}

function animateNodesForMotionInputOrder(graph: AnimationGraph, motionNodeId: string): Extract<AnimationGraphNode, { kind: "animate" }>[] {
  return graph.connections
    .filter(connection => connection.to.nodeId === motionNodeId && connection.to.pin === "motionInput")
    .sort((left, right) => (left.to.index ?? 0) - (right.to.index ?? 0))
    .flatMap(connection => {
      const node = graph.nodes.find(candidate => candidate.id === connection.from.nodeId);
      return node?.kind === "animate" ? [node] : [];
    });
}

function animateNodeGroupTargetCount(graph: AnimationGraph, node: Extract<AnimationGraphNode, { kind: "animate" }>): number {
  const source = animateNodeGroupInput(graph, node);
  return source ? animationGroupTargetCount(graph, source, new Set()) : 1;
}

function animateNodeHasGroupInput(graph: AnimationGraph, node: Extract<AnimationGraphNode, { kind: "animate" }>): boolean {
  return Boolean(animateNodeGroupInput(graph, node));
}

function animateNodeGroupOverridesActive(graph: AnimationGraph, node: Extract<AnimationGraphNode, { kind: "animate" }>): boolean {
  if (!animateNodeHasGroupInput(graph, node)) {
    return false;
  }
  return graph.nodes.some(candidate => {
    if (candidate.kind !== "motion" || (candidate.animateElementsIndependently !== true && candidate.stutterMs <= 0)) {
      return false;
    }
    const animateNodes = animateNodesForMotionInputOrder(graph, candidate.id);
    return animateNodes.length === 1 && animateNodes[0]?.id === node.id;
  });
}

function animateNodeGroupInput(graph: AnimationGraph, node: Extract<AnimationGraphNode, { kind: "animate" }>): Extract<AnimationGraphNode, { kind: "group" }> | undefined {
  const input = graph.connections.find(connection => connection.to.nodeId === node.id && (connection.to.pin === "input" || connection.to.pin === "objectInput"));
  if (!input) {
    return undefined;
  }
  const source = graph.nodes.find(candidate => candidate.id === input.from.nodeId);
  return source?.kind === "group" ? source : undefined;
}

function animationGroupTargetCount(graph: AnimationGraph, node: Extract<AnimationGraphNode, { kind: "group" }>, seen: Set<string>): number {
  if (seen.has(node.id)) {
    return 0;
  }
  const nextSeen = new Set(seen).add(node.id);
  const directCount = node.regroup !== "none" && node.targets.length === 1
    ? Math.max(2, node.regroupOrder?.length ?? 2)
    : node.targets.length;
  const upstreamCount = graph.connections
    .filter(connection => connection.to.nodeId === node.id)
    .reduce((count, connection) => {
      const source = graph.nodes.find(candidate => candidate.id === connection.from.nodeId);
      return count + (source?.kind === "group" ? animationGroupTargetCount(graph, source, nextSeen) : 0);
    }, 0);
  return directCount + upstreamCount;
}

function nextMotionInputIndex(graph: AnimationGraph | undefined, nodeId: string): number {
  const used = new Set(motionInputIndexes(graph, nodeId));
  let index = 0;
  while (used.has(index)) {
    index += 1;
  }
  return index;
}

type Dir = { dx: number; dy: number };
const DIR_RIGHT: Dir = { dx: 1, dy: 0 };
const DIR_LEFT: Dir = { dx: -1, dy: 0 };
const DIR_DOWN: Dir = { dx: 0, dy: 1 };
const DIR_UP: Dir = { dx: 0, dy: -1 };

function pinDir(pin: AnimationPinKind, nodeKind: AnimationNodeKind): Dir {
  if (pin === "motionSequenceOutput" || (pin === "output" && nodeKind === "animation")) return DIR_DOWN;
  if (pin === "motionSequenceInput") return DIR_UP;
  if (isOutputPin(pin)) return DIR_RIGHT;
  return DIR_LEFT;
}

function curvePath(
  from: { x: number; y: number },
  to: { x: number; y: number },
  fromDir: Dir = DIR_RIGHT,
  toDir: Dir = DIR_LEFT,
): string {
  const dist = Math.max(48, Math.hypot(to.x - from.x, to.y - from.y) * 0.45);
  return `M ${from.x} ${from.y} C ${from.x + fromDir.dx * dist} ${from.y + fromDir.dy * dist}, ${to.x + toDir.dx * dist} ${to.y + toDir.dy * dist}, ${to.x} ${to.y}`;
}

function normalizedRect(start: { x: number; y: number }, end: { x: number; y: number }) {
  const x = Math.min(start.x, end.x);
  const y = Math.min(start.y, end.y);
  return { x, y, width: Math.abs(end.x - start.x), height: Math.abs(end.y - start.y) };
}

function rectIntersects(left: { x: number; y: number; width: number; height: number }, right: { x: number; y: number; width: number; height: number }): boolean {
  return left.x <= right.x + right.width && left.x + left.width >= right.x && left.y <= right.y + right.height && left.y + left.height >= right.y;
}

function toggle(values: string[], id: string): string[] {
  return values.includes(id) ? values.filter(value => value !== id) : [...values, id];
}

function merge(left: string[], right: string[]): string[] {
  return [...left, ...right.filter(id => !left.includes(id))];
}
